<script>
import { defineComponent, ref, computed, onMounted, inject, watch, nextTick, markRaw, getCurrentInstance } from 'vue'
import axios from 'axios'
import nlpUtils from "@/common/nlpUtils.js";
import Welcome from './Welcome.vue'
import LandingPage from './LandingPage.vue'
import SubFactorQuestion from './SubFactorQuestion.vue'
import GuidedQuestion from './GuidedQuestion.vue'
import TrackQuestion from './TrackQuestion.vue'
import ClassificationQuestion from './ClassificationQuestion.vue'
import ZoneQuestion from './ZoneQuestion.vue'
import EvaluationSummary from './Summary.vue'
import EvaluationCompare from './Compare.vue'
import DocSummary from "@/components/common/DocSummary.vue"
import EvaluationInvite from "./EvaluationInvite.vue"
import DocumentActivities from "@/components/audit/cDocumentActivities"
import { useClassificationAiAssist } from '@/composables/useClassificationAiAssist'
import { levellingAnchors } from '@rm-ui/levelling/levellingAnchors'
import { askableTrackQuestions, settledTrackAnswers, trackStrandedByFunction } from '@rm-ui/levelling/evaluationTrackOptions'
import utils from '@/common/utils.js';

export default defineComponent({
  name: 'EvaluationHolder',
  components: {
    Welcome,
    LandingPage,
    SubFactorQuestion,
    GuidedQuestion,
    TrackQuestion,
    ClassificationQuestion,
    ZoneQuestion,
    EvaluationSummary,
    EvaluationCompare,
    DocSummary,
    EvaluationInvite,
    DocumentActivities,
  },
  props: {
    action: {
      type: Object,
      required: false
    },
    docReference: {
      type: String,
      required: false
    },
    isNew: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['saveAnswer', 'actionTriggered', 'close', 'toggleHidden', 'openDocument', 'copyDocument'],
  setup(props, { emit }) {
    const loginState = inject('loginState');
    const store = inject('store');
    const instance = getCurrentInstance();
    const router = inject('router');

    const docListLoading = computed(() => store.state.docs.loading)
    const evaluationData = ref(null)
    const functions = computed(() => evaluationData.value?.functions ?? [])
    const tracks = computed(() => evaluationData.value?.tracks ?? [])
    const levels = computed(() => evaluationData.value?.levels ?? [])
    const factors = computed(() => evaluationData.value?.factors ?? [])
    const functionTrackLevels = computed(() => evaluationData.value?.functionTrackLevels ?? [])
    const alternateQuestions = computed(() => evaluationData.value?.alternateQuestions ?? [])
    const subfactorLevelMappings = computed(() => evaluationData.value?.subfactorLevelMappings ?? [])
    const trackQuestions = computed(() => evaluationData.value?.trackQuestions ?? [])
    const editAction = computed(() => {
      if (!evaluationData.value || !evaluationData.value.workflowActions)
        return null
      const actions = evaluationData.value.workflowActions.filter(a => a.data_capture_method === 'questionnaire' && !a.isDataCaptureViewer)
      if (actions.length === 0)
        return null

      return actions.find(a => !a.allow_delete) ?? actions[0]
    })
    const deleteAction = computed(() => {
      if (!evaluationData.value || !evaluationData.value.workflowActions)
        return null
      return evaluationData.value.workflowActions.find(a => a.data_capture_method === 'questionnaire' && a.allow_delete)
    })
    const viewAction = computed(() => evaluationData.value?.workflowActions?.find(a => a.data_capture_method === 'questionnaire' && a.isDataCaptureViewer))
    const readOnly = computed(() => !editAction.value)

    const showDocDetails = computed(() => {
      //hide if no doc or if used from within existing document (have props.action)
      if (!doc.value || props.action)
        return false;

      //welcome: the re-evaluate ("Confirm the role and document") and upload
      //("Add the role") steps preview the doc in this shared column
      if (viewState.value === 'welcome')
        return ['docPreview', 'preview'].includes(welcomeSubState.value);

      //hide for the remaining chrome-only states
      if (['loading', 'invite', 'landing', 'error', 'summary', 'comparison', 'thankyou'].includes(viewState.value))
        return false;

      return true;
    })
    const aiAssist = useClassificationAiAssist()
    const fullDocument = ref(null)

    function _stripHtmlToText(html) {
      if (!html) return ''
      const div = document.createElement('div')
      div.innerHTML = html
      return (div.textContent || '').trim()
    }

    // Mirror DocSummaryPartType's render so we collect the same content the user
    // sees. Each partType renders its parts (via attributes or sub_parts), and each
    // part may carry nested childPartTypes which are rendered recursively.
    function _collectPartTypeText(pt, sections) {
      for (const p of (pt.parts || [])) {
        // When usesAttributes is true, the part is rendered via attributes (and any
        // tagValues table); otherwise via sub_parts.
        if (p.usesAttributes && Array.isArray(p.attributes)) {
          for (const attr of p.attributes) {
            // Tag-driven attributes (e.g. skills) carry their displayed content
            // on tagValues. Include name + descriptor only — proficiency
            // (rating_name) is intentionally excluded so it can't anchor evidence.
            if (Array.isArray(attr.tagValues) && attr.tagValues.length) {
              for (const tv of attr.tagValues) {
                const name = (tv.value || '').toString().trim()
                const descriptor = _stripHtmlToText(tv.rating_description)
                if (name && descriptor) {
                  sections.push(`${name}\n${descriptor}`)
                } else if (descriptor) {
                  sections.push(descriptor)
                } else if (name) {
                  sections.push(name)
                }
              }
            } else {
              const t = _stripHtmlToText(attr.text)
              if (t) sections.push(t)
            }
          }
        } else if (Array.isArray(p.sub_parts)) {
          for (const sp of p.sub_parts) {
            const t = _stripHtmlToText(sp.text)
            if (t) sections.push(t)
          }
        }
        // Recurse into nested child part types, matching DocSummaryPartType.
        if (Array.isArray(p.childPartTypes) && p.childPartTypes.length) {
          for (const cpt of p.childPartTypes) {
            _collectPartTypeText(cpt, sections)
          }
        }
      }
    }

    const jdPlainText = computed(() => {
      if (!fullDocument.value || !fullDocument.value.partTypes) return ''
      const sections = []
      for (const pt of fullDocument.value.partTypes) {
        _collectPartTypeText(pt, sections)
      }
      return sections.filter(t => t).join('\n\n')
    })

    const aiSourceText = computed(() => {
      if (contentSource.value.type === 'file') {
        // An uploaded file is the source. Until its text has been fetched
        // (fetchFileData is not awaited on resume) the source is NOT READY —
        // it must never fall back to the system document, or the assist runs
        // on the wrong text: "N matches found", nothing to highlight in the
        // uploaded document, and an AI rationale grounded on text the evaluator
        // cannot see. Empty makes the trigger wait, and the empty→non-empty
        // watcher below starts it once the file text lands.
        const fileContent = contentSource.value.content
        if (!fileContent) return ''
        const div = document.createElement('div')
        div.innerHTML = fileContent
        return div.textContent || ''
      }
      return jdPlainText.value
    })

    const processing = ref(false)
    const openSummaryEvent = ref(null)
    const doc = ref(null)
    const task = ref(null)
    const relatedContentLoading = ref(false)
    const currentFactorIndex = ref(0)
    const currentSubFactorIndex = ref(0)
    const editingFromSummary = ref(false)
    const editingTrackFromSummary = ref(false)
    const answers = ref([])
    const guidedAnswers = ref([])
    const visibleQuestionIds = ref([])
    const currentGuidedIndex = ref(0)
    const trackAnswers = ref([])
    const currentTrackIndex = ref(0)
    const classificationAnswers = ref([])
    const currentClassificationIndex = ref(0)
    const contentSource = ref({
      type: "document",
      file_id: null,
      row_id: null,
      file_name: null,
      content: null
    })
    const viewState = ref('loading')
    // The Welcome step drives its own internal sub-state; we mirror it here so the
    // shared job-document column can appear alongside its "Confirm the role and
    // document" step, matching the later evaluation steps.
    const welcomeSubState = ref(null)
    // Extracted job-description preview emitted by Welcome during its upload step,
    // rendered in the shared right-hand column.
    const thankYouTitle = ref('Thank you')
    /* Copy for a thank-you that ISN'T a completed evaluation (the invite hand-off).
       Null leaves the completion message to `completionMessage`, which names the flow
       the user actually took — see below. */
    const thankYouMessageOverride = ref(null)
    const welcomePreview = ref({ fileName: '', contentHtml: '', loading: false })
    const errorMessage = ref('')
    // Set at load — see the assignment in initialize().
    const hasCompletedEvaluation = ref(false)
    const showRestartConfirm = ref(false)
    /* Set at load when an existing evaluation's method cannot stand under the anchor
       intent, so the journey enters at the re-evaluate step instead of the method
       screen. See the normalisation in loadEvaluationDataFromApi. */
    const anchorNeedsRelevel = ref(false)

    const showDeleteConfirm = ref(false)
    const deleting = ref(false)
    const showGuidance = ref(false)
    const showActivity = ref(false)

    function openActivity() {
      showActivity.value = true
    }

    function activityClosed() {
      showActivity.value = false
    }

    // Doc-details panel can be expanded to take more width from the evaluation
    // column: default 7/5 (evaluation/doc), expanded 5/7.
    const docDetailsExpanded = ref(false)
    function toggleDocDetailsExpanded() {
      docDetailsExpanded.value = !docDetailsExpanded.value
    }
    // Header shows the uploaded file name when the source is a file, otherwise
    // the job document's name.
    const docHeaderTitle = computed(() => {
      if (contentSource.value.type === 'file')
        return contentSource.value.file_name || doc.value?.doc_name || 'Uploaded document'
      return doc.value?.doc_name || 'Job document'
    })
    const docHeaderSubtitle = computed(() => {
      if (viewState.value === 'welcome' && welcomePreview.value.contentHtml || contentSource.value.type === 'file')
        return 'Uploaded document'
      return doc.value?.doc_type ? `${doc.value.doc_type} record` : 'Document record'
    })

    const evaluation = ref({
      id: null,
      flowType: null,
      classificationZoneIndex: null,
      suggestedLevelId: null,
      selectedLevelId: null,
      comments: null,
      status: 'DRAFT',
      // Anchor roles (PD-1258): the intent captured on the method screen / summary.
      // Written to document_lf_anchor server-side when the evaluation completes.
      isAnchor: false,
      anchorRationale: null,
      isCeiling: false,
    })

    /* The completion message names the route the user actually took. The three flows
       are presented under their own names on the method screen (Evaluate / Guided
       questionnaire / Classify), so telling someone who answered the full evaluation
       that "the level selected during your classification has been applied" describes
       a journey they didn't take. */
    const FLOW_NOUNS = {
      standard: 'evaluation',
      guided: 'guided questionnaire',
      classification: 'classification',
    }
    const completionMessage = computed(() => {
      const noun = FLOW_NOUNS[evaluation.value?.flowType] ?? 'evaluation'
      return `The level selected during your ${noun} has been applied to the selected job. `
        + 'The scores and rationale provided will be visible to relevant users within your organisation.'
    })
    const thankYouMessage = computed(() => thankYouMessageOverride.value ?? completionMessage.value)

    /* ---- Anchor roles (PD-1258) --------------------------------------------
       Inert while the `levelling_anchor_roles` client setting is off. Two jobs
       in this flow:
         1. the CEO ceiling removes levels at/above it from CLASSIFICATION, and
         2. an evaluation landing at/above it is flagged for review (advisory).
       `anchorIntent` arrives as ?anchor=1 from the control centre's "Evaluate as
       anchor role" action, and locks the journey to a full evaluation. */
    const anchors = levellingAnchors({
      http: axios,
      getUser: () => loginState?.user,
    })
    /* markRaw'd before it crosses a prop boundary: Vue 2.7's observer walks the
       object and unwraps its inner refs into plain values, after which `.value`
       writes inside the composable throw. */
    const anchorsRaw = markRaw(anchors)
    const anchorsEnabled = computed(() => anchors.enabled.value)
    const canGovernAnchors = computed(() => anchors.canGovern.value)
    // Anchors need a full evaluation, so the intent forces flowType 'standard'.
    const anchorIntent = computed(() => !!evaluation.value.isAnchor)
    const ceiling = computed(() => (anchorsEnabled.value ? anchors.ceiling.value : null))
    /* The role's job family, for "the calibration reference other X roles are compared
       against". The journey's own doc payload has no family, so this reads it from the
       anchor record (getLfAnchorList now returns it) and falls back to the doc if a
       client ever starts sending one. Null leaves the copy on its generic wording. */
    const anchorFamily = computed(() => {
      const info = doc.value ? anchors.anchorInfo(doc.value.doc_id) : null
      return (info && info.family) || (doc.value && doc.value.family) || null
    })
    /* No CEO-ceiling intent in the journey: the design prototype only offers the
       ceiling from an already-anchored, already-evaluated role's record, so it is set
       from the levelling record's anchor chip. `evaluation.isCeiling` remains in the
       payload (the server still reads it) but the journey never sets it. */
    
    async function loadFullDocument() {
      if (!doc.value) return
      try {
        const resp = await store.dispatch('docs/getDocumentSummary', doc.value.doc_id)
        if (resp?.data?.Data?.documents?.length) {
          fullDocument.value = resp.data.Data.documents[0]
        }
      } catch (err) {
        console.error('Failed to load full document for AI assist:', err)
      }
    }

    watch(doc, (newDoc) => {
      if (newDoc) loadFullDocument()
    })
    watch(() => props.docReference, () => {
      initialize()
    })

    // If startClassification fires before fullDocument has loaded, _getText()
    // returns empty and the chain trigger is skipped. Re-fire prefetch as soon
    // as the source text becomes available so the user isn't stuck.
    watch(aiSourceText, async (newText, oldText) => {
      const justReady = newText && (!oldText || oldText.length === 0)
      const inAssistedFlow = ['classification-question', 'classification-zone', 'sub-factor'].includes(viewState.value)
      if (justReady && inAssistedFlow) {
        // Restart rather than just prefetch: the earlier start ran its
        // reuse-existing-runs lookup against empty text (nothing could match),
        // so run it again now that the text — and its hash — are known. Nothing
        // is lost by the reset: every trigger before this point was skipped.
        await aiAssist.startClassification(() => aiSourceText.value, doc.value?.system_number)
        aiAssist.ensurePrefetched(aiCurrentUnitIndex.value || 0)
      }
    })

    watch(
      () => [
        viewState.value,
        currentSubFactorIndex.value,
        currentFactorIndex.value,
        currentTrackIndex.value,
        currentGuidedIndex.value,
        currentClassificationIndex.value,
      ],
      () => {
        showGuidance.value = false
      }
    )

    const guidanceText = computed(() => {
      switch (viewState.value) {
        case 'welcome':
          return "Welcome to the evaluation. Upload the job description or skip to start from scratch — you can always add it later."
        case 'landing':
          return "Choose how you'd like to evaluate this role. Each path leads to the same outcome — pick the one that best matches the information you have."
        case 'track-question':
          return "Answer the track questions to help us identify the most appropriate career track for this role."
        case 'sub-factor':
          return "Pick the level that best describes the role for this sub-factor. If multiple levels could apply, choose the one that fits the majority of the work."
        case 'classification-zone':
          return "Select the zone that best represents the scope and impact of this role within the organisation."
        case 'classification-question':
          return "Answer each question to classify the role. Your responses will be combined to suggest an overall level."
        case 'guided-question':
          return "Work through the guided questions one at a time. You can go back to revise earlier answers if needed."
        case 'summary':
          return "Review the suggested level and your answers. You can adjust the final level, add comments, or compare against other roles before saving."
        case 'comparison':
          return "Compare this evaluation against other roles to sense-check the suggested level."
        case 'thankyou':
          return "The evaluation has been saved. You can return to it later from the document view."
        case 'error':
          return "Something went wrong loading this evaluation. Try again, or contact support if the problem persists."
        case 'loading':
        default:
          return "Loading the evaluation. This should only take a moment."
      }
    })

    const currentFactor = computed(() => factors.value[currentFactorIndex.value])

    const currentSubFactor = computed(() => {
      if (!currentFactor.value) return null
      return currentFactor.value.subFactors[currentSubFactorIndex.value]
    })

    const totalSubFactors = computed(() => {
      return factors.value.reduce((sum, f) => sum + f.subFactors.length, 0)
    })

    const currentGlobalSubFactorIndex = computed(() => {
      let count = 0
      for (let i = 0; i < currentFactorIndex.value; i++) {
        const factor = factors.value[i]
        if (factor) {
          count += factor.subFactors.length
        }
      }
      return count + currentSubFactorIndex.value
    })

    // Coloured factor stepper shown above the sub-factor question. Colours mirror
    // Only factors before the current one are
    // clickable (jump back) — no jumping forward to skip questions.
    function factorIsAnswered(f) {
      return (f.subFactors || []).every(sf => answers.value.some(a => a.factorId === f.id && a.subFactorId === sf.id))
    }
    const factorSteps = computed(() => factors.value.map((f, i) => ({
      name: f.name,
      color: utils.shadeColor(f.backgroundColour, 20, -20),
      done: factorIsAnswered(f),
      current: i === currentFactorIndex.value,
      // Navigable in either direction, but only to a factor that's already complete
      // (or any earlier one) — never jump ahead over an unanswered factor.
      clickable: i !== currentFactorIndex.value && (i < currentFactorIndex.value || factorIsAnswered(f)),
    })))

    function goToFactor(index) {
      if (editingFromSummary.value || index === currentFactorIndex.value) return
      // Forward jumps are allowed only into an already-completed factor, so an
      // unanswered factor can never be skipped over.
      if (index > currentFactorIndex.value && !factorIsAnswered(factors.value[index])) return
      currentFactorIndex.value = index
      currentSubFactorIndex.value = 0
      viewState.value = 'sub-factor'
    }

    // Whether this document offers "invite a colleague" (or other non-questionnaire)
    // workflow actions, and whether the invite fork should be shown at all: only
    // when the current user isn't the assigned evaluator (no task) — the fork now
    // sits after the method choice rather than as the entry gate.
    const hasInviteActions = computed(() =>
      (evaluationData.value?.workflowActions ?? []).filter(a => a.data_capture_method !== 'questionnaire').length > 0
    )
    const showInviteGate = computed(() => !task.value && hasInviteActions.value)
    // Method chosen before the invite fork, held so it can be re-applied/persisted
    // once the "evaluate now" workflow action has granted edit permission.
    const pendingFlowType = ref(null)
    // User has been invited (as opposed to evaluating themselves).
    const inviteResolved = ref(null)

    // Overall flow stepper (indicates position in the end-to-end journey). Each
    // stage maps to one or more viewStates; states not in the list (loading, error,
    // invite) hide the stepper.
    const FLOW_STEPS = [
      { label: 'Method', states: ['landing'] },
      { label: 'Document', states: ['welcome'] },
      { label: 'Track', states: ['track-question'] },
      { label: 'Levels', states: ['sub-factor', 'classification-zone', 'classification-question', 'guided-question'] },
      { label: 'Summary', states: ['summary', 'comparison'] },
      { label: 'Confirm', states: ['thankyou'] },
    ]
    const currentFlowIndex = computed(() => FLOW_STEPS.findIndex(s => s.states.includes(viewState.value)))

    // ---- Flow-step completion / reachability (drives click-navigation) --------
    function flowTrackComplete() {
      if (trackQuestions.value.length === 0) return true
      return trackQuestions.value.every(tq => trackAnswers.value.some(a => a.questionNo === tq.no && a.selectedKey != null))
    }
    function flowLevelsComplete() {
      const ft = evaluation.value.flowType
      if (ft === 'classification') {
        if (evaluation.value.classificationZoneIndex === null) return false
        return classificationFactors.value.length > 0 &&
          classificationFactors.value.every(f => classificationAnswers.value.some(a => a.factorName === f.name))
      }
      if (ft === 'guided') {
        return visibleQuestionIds.value.length > 0 &&
          visibleQuestionIds.value.every(qid => guidedAnswers.value.some(a => a.questionId === qid))
      }
      if (ft === 'standard') {
        return factors.value.length > 0 &&
          factors.value.every(f => (f.subFactors || []).every(sf => answers.value.some(a => a.factorId === f.id && a.subFactorId === sf.id)))
      }
      return false
    }
    function flowStepComplete(i) {
      switch (FLOW_STEPS[i]?.label) {
        case 'Document': return !!evaluation.value.flowType
        case 'Method': return !!evaluation.value.flowType
        case 'Track': return !!evaluation.value.flowType && flowTrackComplete()
        case 'Levels': return !!evaluation.value.flowType && flowLevelsComplete()
        case 'Summary':
        case 'Confirm': return evaluation.value.status === 'COMPLETED'
        default: return false
      }
    }
    function flowStepApplicable(i) {
      const label = FLOW_STEPS[i]?.label
      if (label === 'Track') return askableQuestions.value.length > 0
      if (label === 'Confirm') return evaluation.value.status === 'COMPLETED'
      return true
    }
    // Reachable only when every applicable earlier step is complete — so a click
    // can never skip over an uncompleted step (but may go back or forward freely
    // among the steps whose prerequisites are all met).
    function flowStepReachable(i) {
      // don't allow manual navigation to confirm, method and document steps
      // confirm step is only shown once after clicking accept/save
      const stepLabel = FLOW_STEPS[i]?.label
      if (['Confirm', 'Method', 'Document'].includes(stepLabel))
          return false

      for (let j = 0; j < i; j++) {
        if (flowStepApplicable(j) && !flowStepComplete(j)) return false
      }
      return true
    }

    const flowSteps = computed(() => {
      const cur = currentFlowIndex.value
      let steps = FLOW_STEPS
      if (showInviteGate.value && inviteResolved.value && inviteResolved.value.length > 0) {
        steps = FLOW_STEPS.filter(s => ['Document', 'Method'].includes(s.label))
      }
      return steps.map((s, i) => ({
          label: s.label,
          state: i === cur ? 'current' : flowStepComplete(i) ? 'done' : 'future',
          clickable: i !== cur && flowStepApplicable(i) && flowStepReachable(i),
        }))
    })
    
    const showFlowStepper = computed(() => evaluation.value && evaluation.value.status === 'DRAFT' && !props.action && currentFlowIndex.value >= 0)

    // When the full stepper is hidden (completed / action / read-only views) but
    // we're still on a recognised stage, surface just that stage's name so the
    // top bar isn't blank.
    const currentStepLabel = computed(() => FLOW_STEPS[currentFlowIndex.value]?.label ?? '')

    async function goToFlowStep(i) {
      if (i === currentFlowIndex.value || !flowStepApplicable(i) || !flowStepReachable(i)) return
      editingFromSummary.value = false
      editingTrackFromSummary.value = false
      switch (FLOW_STEPS[i].label) {
        case 'Document':
          viewState.value = 'welcome'
          break
        case 'Method':
          viewState.value = 'landing'
          break
        case 'Track':
          await enterTrackQuestions()
          break
        case 'Levels':
          if (evaluation.value.flowType === 'classification') {
            if (evaluation.value.classificationZoneIndex === null) {
              viewState.value = 'classification-zone'
            } else {
              currentClassificationIndex.value = 0
              aiAssist.startClassification(() => aiSourceText.value, doc.value?.system_number)
              aiAssist.ensurePrefetched(0)
              viewState.value = 'classification-question'
            }
          } else if (evaluation.value.flowType === 'guided') {
            currentGuidedIndex.value = 0
            viewState.value = 'guided-question'
          } else {
            currentFactorIndex.value = 0
            currentSubFactorIndex.value = 0
            aiAssist.startClassification(() => aiSourceText.value, doc.value?.system_number)
            aiAssist.ensurePrefetched(0)
            viewState.value = 'sub-factor'
          }
          break
        case 'Summary':
          if (evaluation.value.flowType === 'classification') convertClassificationToEvaluationAnswers()
          else if (evaluation.value.flowType === 'guided') convertGuidedToEvaluationAnswers()
          viewState.value = 'summary'
          break
        case 'Confirm':
          viewState.value = 'thankyou'
          break
      }
    }

    const previousAnswer = computed(() => {
      const factor = currentFactor.value
      const subFactor = currentSubFactor.value
      if (!factor || !subFactor) return undefined
      const existing = answers.value.find(
        a => a.factorId === factor.id && a.subFactorId === subFactor.id
      )
      return existing
    })

    const currentGuidedQuestion = computed(() => {
      const qid = visibleQuestionIds.value[currentGuidedIndex.value]
      return alternateQuestions.value.find(q => q.id === qid)
    })

    const previousGuidedAnswer = computed(() => {
      if (!currentGuidedQuestion.value) return undefined
      const existing = guidedAnswers.value.find(a => a.questionId === currentGuidedQuestion.value.id)
      return existing
    })

    // askableQuestions (below) is the list this walks — see the note there.
    const currentTrackQuestion = computed(() => askableQuestions.value[currentTrackIndex.value])

    const previousTrackAnswer = computed(() => {
      if (!currentTrackQuestion.value) return undefined
      const existing = trackAnswers.value.find(a => a.questionNo === currentTrackQuestion.value.no)
      return existing?.selectedKey
    })

    const selectedFunction = computed(() => {
      if (trackAnswers.value.length === 0) return null
      const trackAnswer = trackAnswers.value.find(a => a.questionNo === 1)

      return functions.value.find(t => t.id === trackAnswer?.selectedKey) || null
    })

    const selectedTrack = computed(() => {
      if (trackAnswers.value.length === 0) return null
      const trackAnswer = trackAnswers.value.find(a => a.questionNo === 2)

      return tracks.value.find(t => t.id === trackAnswer?.selectedKey) || null
    })

    /* The track questions the user is actually asked (PD-1324b).

       Two things narrow the framework's raw lists. The career-track options are
       cut to the chosen discipline — a (discipline, track) pairing with no rows in
       functionTrackLevels has no levels to land on, so the evaluation it starts
       could never resolve. And a question left with a single option is no choice
       at all, so it is answered on the user's behalf (applySettledTrackAnswers)
       and dropped from the list rather than put to them.

       `currentTrackIndex` walks THIS list, so the "question n of m" count, the
       Previous button and the end-of-track hand-off to the levels all follow. */
    const framework = computed(() => ({
      functions: functions.value, tracks: tracks.value, functionTrackLevels: functionTrackLevels.value,
    }))
    const askableQuestions = computed(() =>
      askableTrackQuestions(trackQuestions.value, framework.value, trackAnswers.value))

    /* Record the answers the framework settles by itself. Idempotent, and returns
       whether anything moved so the caller can decide to persist. Cascades: a
       single-discipline framework settles question 1, which can narrow question 2
       to one track and settle that too. */
    function applySettledTrackAnswers() {
      const settled = settledTrackAnswers(trackQuestions.value, framework.value, trackAnswers.value)
      settled.forEach(a => setTrackAnswer(a.questionNo, a.selectedKey))
      return settled.length > 0
    }

    /* Enter the track stage — or step straight over it when nothing is left to ask.
       Every route into the track questions goes through here so the settled answers
       are recorded (and saved) exactly once, wherever the user came from. */
    async function enterTrackQuestions() {
      const settled = applySettledTrackAnswers()
      if (settled) await saveEvaluation()
      if (!askableQuestions.value.length) {
        await startSelectedFlow()
        return
      }
      currentTrackIndex.value = 0
      viewState.value = 'track-question'
    }

    const currentClassificationFactor = computed(() => {
      return factors.value[currentClassificationIndex.value] || null
    })

    const currentClassificationFactorName = computed(() => {
      return currentClassificationFactor.value?.name || null
    })

    const trackLevels = computed(() => {
      if (!selectedTrack.value || !selectedFunction.value) return []
      return functionTrackLevels.value.filter(ftl =>
        ftl.trackId === selectedTrack.value?.id &&
        ftl.functionId === selectedFunction.value?.id) || []
    })

    // The selected level's abbreviation within the chosen track/function
    // (e.g. "IC5"), for the completed-record pill. Falls back to the bare level
    // number, then to the evaluation's stored level.
    const selectedLevelAbbreviation = computed(() => {
      const levelName = levels.value.find(l => l.id === evaluation.value.selectedLevelId)?.name
      if (levelName == null) return null
      const trackLevel = trackLevels.value.find(tl => Number(tl.level) === Number(levelName))
      return trackLevel?.abbreviation ?? levelName
    })

    const classificationFactors = computed(() => factors.value.filter(f => f.weighting > 0))

    /* THE CEILING CHOKE POINT (anchor roles, PD-1258).
       Levels at or above the CEO ceiling are not offered as placement options
       during CLASSIFICATION — shown WHY inline, never silently. Everything the
       classify path offers derives from here (zones → the zone selector → the
       per-factor level list → ClassificationQuestion's "Show all levels"), so
       the escape hatch is closed by construction rather than patched.

       `trackLevels` itself is deliberately left untouched: scoring must still be
       able to LAND above the ceiling so the outcome can be flagged for review.
       Fails open — if filtering would empty the track, the full ladder stands. */
    const classificationTrackLevels = computed(() => anchors.levelsBelowCeiling(trackLevels.value))
    const ceilingAppliesToTrack = computed(() =>
      anchorsEnabled.value && anchors.ceilingApplies(trackLevels.value))
    /* One source for the "why the top is missing" note — it appears on BOTH
       classification steps (zone and per-factor), which are filtered by the same
       choke point. Null unless the ceiling actually removed levels from this track. */
    const ceilingNote = computed(() =>
      ceilingAppliesToTrack.value && ceiling.value
        ? `Levels at or above your framework ceiling aren’t offered — ${ceiling.value.title} anchors the top at ${ceiling.value.levelCode || 'its level'}. If this role genuinely belongs there, evaluate it instead; the outcome will be flagged for review, not blocked.`
        : null)

    const classificationZones = computed(() => {
      if (!selectedTrack.value) return []
      const list = classificationTrackLevels.value
      if (list.length < 3) return [{ levels: list }]

      const zones = []
      for (let i = 0; i <= list.length - 3; i++) {
        zones.push({ levels: list.slice(i, i + 3) })
      }
      return zones
    })

    /* ---- Published grade link (PD-1293) -------------------------------------
       Where the client has published a read-across from its own grade chart, the
       role's grade lines up with a RoleMapper level, and the zone step offers that
       as its starting point. A SUGGESTION ONLY: it pre-selects the zone and says why
       inline, and nothing is written until the person confirms it with Next — the
       same answer path as picking a zone by hand. The server says nothing unless
       the whole chain holds (graded, published on this ladder, chart live, grade
       linked); the cases it CAN'T offer on this track are explained here, because
       they are the ones that would otherwise look like the link was ignored. */
    const gradeLink = ref(null)
    async function loadGradeLink() {
      gradeLink.value = null
      const docId = doc.value?.doc_id
      if (!docId) return
      try {
        const resp = await axios.post('document/gradeLinkSuggestion', { doc_id: docId })
        gradeLink.value = resp?.data?.Status === 'OK' ? (resp.data.Data?.suggestion || null) : null
      } catch (err) {
        // Optional by nature: failing to fetch a suggestion must never block levelling.
        gradeLink.value = null
      }
    }

    const zoneSuggestion = computed(() => {
      const link = gradeLink.value
      if (!link || evaluation.value.flowType !== 'classification' || !selectedTrack.value) return null
      const source = `${link.schemeTitle} ${link.grade}`
      const published = link.publishedDate
        ? `, published ${new Date(link.publishedDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`
        : ''
      const at = (classificationTrackLevels.value || []).findIndex(tl => Number(tl.levelId) === link.lf_level_id)
      if (at >= 0) {
        const code = classificationTrackLevels.value[at].abbreviation || `Level ${link.levelName}`
        return {
          levelIndex: at,
          prefillText: `Pre-filled from your published grade link (v${link.versionNo}${published}): ${source} lines up with ${code}. `
            + 'It is a starting point, not a decision — check it against the role, then confirm the zone or pick another.',
          infoText: `Your published grade link lines up ${source} with ${code}.`,
        }
      }
      // Linked, but not a level this track offers: say why rather than stay silent.
      const onTrack = (trackLevels.value || []).find(tl => Number(tl.levelId) === link.lf_level_id)
      const infoText = onTrack
        ? `Your published grade link lines up ${source} with ${onTrack.abbreviation || `Level ${link.levelName}`}, which is at or above your framework ceiling, so it is not offered here. Choose the zone yourself.`
        : `Your published grade link lines up ${source} with Level ${link.levelName}, which the ${selectedTrack.value.name} track does not have. Choose the zone yourself.`
      return { levelIndex: null, prefillText: null, infoText }
    })

    const selectedZoneLevels = computed(() => {
      if (evaluation.value.classificationZoneIndex === null) return null
      const zone = classificationZones.value[evaluation.value.classificationZoneIndex]
      return zone ? zone.levels.map(l => l.level) : null
    })

    const classificationLevelsForCurrentFactor = computed(() => {
      const zoneLevels = selectedZoneLevels.value
      if (zoneLevels && zoneLevels.length)
        return allClassificationLevelsForCurrentFactor.value.filter((tl => zoneLevels.includes(tl.level)))
      else
        return allClassificationLevelsForCurrentFactor.value
    })

    const allClassificationLevelsForCurrentFactor = computed(() => {
      if (!selectedTrack.value || !currentClassificationFactor.value) return []
      // Ceiling-filtered (see classificationTrackLevels): this feeds both the
      // zone-narrowed list AND ClassificationQuestion's "Show all levels".
      return (classificationTrackLevels.value || []).map(tl => {
        return {
          ...tl,
          description: (tl.factorDescriptions ? tl.factorDescriptions[currentClassificationFactor.value.id] : null) ?? 'Missing descriptor...'
        }
      })
    })

    const previousClassificationAnswer = computed(() => {
      if (!currentClassificationFactor.value) return undefined
      const existing = classificationAnswers.value.find(a => a.factorName === currentClassificationFactor.value.name)
      return existing
    })

    onMounted(async () => {
      if (docListLoading.value) {
        viewState.value = 'loading'
        const unwatch = watch(docListLoading, (newVal) => {
          if (!newVal) {
            unwatch()
            initialize()
          }
        })
      } else {
        initialize()
      }
    })

    async function initialize() {
      try {
        // Anchor roles (PD-1258): resolve the client's ceiling + this doc's anchor
        // before the first render, so classification never briefly offers a level
        // the ceiling forbids. Self-skips while the feature is off.
        const [data] = await Promise.all([
          loadEvaluationDataFromApi(),
          anchors.load(),
        ])
        doc.value = data.document
        evaluationData.value = data.evaluation
        // Not awaited: the zone step picks it up when it lands, and never waits for it.
        loadGradeLink()
        /* Whether the ROLE has ever been levelled, which is not the same as the
           current evaluation's status: a re-evaluation is a fresh DRAFT, so the
           completed run it replaces is no longer the one loaded. Only the welcome
           step's re-evaluate intro ("the previous answers are pre-filled") depends
           on it, and restart() must not clear it — hence a ref of its own rather
           than a field on `evaluation`. */
        hasCompletedEvaluation.value = !!data.hasCompletedEvaluation
        // "Evaluate as anchor role" from the control centre arrives as ?anchor=1.
        const routeQuery = instance?.proxy?.$route?.query || {}
        const routeAnchorIntent = anchorsEnabled.value
          && canGovernAnchors.value
          && String(routeQuery.anchor || '') === '1'
        const existingAnchor = doc.value ? anchors.anchorInfo(doc.value.doc_id) : null
        if (data.response) {
          evaluation.value = {
            id: data.response.evaluationId,
            flowType: data.response.flowType?.toLowerCase(),
            classificationZoneIndex: data.response.classificationZoneIndex,
            suggestedLevelId: data.response.suggestedLevelId,
            selectedLevelId: data.response.selectedLevelId,
            score: data.response.score,
            comments: data.response.comments,
            status: data.response.status,
            updated_by: data.response.updated_by,
            updated_date: data.response.updated_date,
            // An already-anchored role keeps its mark and rationale; the route
            // intent can only turn it on.
            isAnchor: !!existingAnchor || routeAnchorIntent,
            anchorRationale: (existingAnchor && existingAnchor.rationale) || null,
          }
          /* An anchor needs a full, point-scored evaluation, so an existing
             classification (or guided run) cannot stand — the pair is normalised here
             rather than relying on the user passing the method screen again.
             It resolves to 'standard' rather than to null. Blanking it dropped the user
             on "How would you like to level this role?" with exactly ONE permitted
             answer (Classify renders locked under the intent), which is a dead click —
             and worse, it skipped the re-evaluate step entirely, so nothing explained
             that an evaluation already existed or that its method was being changed.
             `anchorNeedsRelevel` makes setInitialView enter at that step instead, which
             is where "Evaluate to anchor" from a summary already lands (restartAsAnchor). */
          if (evaluation.value.isAnchor && evaluation.value.flowType && evaluation.value.flowType !== 'standard') {
            evaluation.value.flowType = 'standard'
            anchorNeedsRelevel.value = true
          }
          if (data.response.file_id) {
            contentSource.value.type = 'file'
            contentSource.value.file_id = data.response.file_id
            contentSource.value.row_id = data.response.row_id
            contentSource.value.file_name = data.response.file_name
            fetchFileData()
          } else {
            contentSource.value.type = 'document'
            contentSource.value.file_id = null
            contentSource.value.row_id = null
            contentSource.value.file_name = null
          }
          trackAnswers.value.push(...data.response.trackAnswers)
          classificationAnswers.value.push(...data.response.classificationAnswers)
          answers.value.push(...data.response.standardAnswers)
          guidedAnswers.value.push(...data.response.guidedAnswers)
        } else if (routeAnchorIntent || existingAnchor) {
          // Brand-new evaluation opened via "Evaluate as anchor role", or on a role
          // that already carries the mark (anchor roles, PD-1258).
          evaluation.value.isAnchor = true
          evaluation.value.anchorRationale = (existingAnchor && existingAnchor.rationale) || null
        }

        /* Settle the track answers the framework leaves no choice over BEFORE the
           first view is picked: the resume logic and the flow stepper both read
           trackAnswers, and a question that will never be asked must not read as
           unanswered. Persisted on the way into (or over) the track stage. */
        applySettledTrackAnswers()

        if (factors.value.length === 0) {
          viewState.value = 'error'
          errorMessage.value = 'No evaluation data found.'
          return
        }

        if (!editAction.value && !viewAction.value) {
          viewState.value = 'error'
          errorMessage.value = 'You are not permitted to view this evaluation.'
          return
        }

        setInitialView()

      } catch (error) {
        console.error('Failed to load evaluation data:', error)
        viewState.value = 'error'
        errorMessage.value = 'Failed to load evaluation data.'
      }
    }

    function setInitialView() {
      /* Re-levelling an existing evaluation because the anchor intent bars its method.
         Checked BEFORE the resume logic below: that would follow the answers already
         given and jump straight to a factor question, skipping the one step whose job
         is to say "you are re-evaluating this role, and here is why". */
      if (anchorNeedsRelevel.value) {
        viewState.value = 'welcome'
        return
      }
      //evaluation in progress
      if (evaluation.value.id) {
        selectInitialQuestion()
        return
      }

      const tasks = store.getters["tasks/list"];
      const evaluateTask = tasks.find(t => t.mine &&
          t.reference === props.docReference &&
          t.task_action === 'LEVELLING_EVALUATION');
      task.value = evaluateTask || null;

      /* Nothing started, and no questionnaire edit action to start it with — the
         journey's own entry points (welcome / landing) would take answers that
         cannot be saved, so say so instead.
         The invite fork is NOT that: a user may hold only the non-questionnaire
         workflow actions ("invite a colleague", "evaluate now"), which is exactly
         how the edit permission gets granted — processing one and re-initialising
         is what lifts readOnly. So it is checked here, after the invite gate is
         known, rather than on the way into this function. */
      if (readOnly.value && !showInviteGate.value) {
        raiseError('This evaluation has not been started yet and you do not have permission to start it.')
        return
      }

      if (evaluateTask)
        viewState.value = 'welcome';
      else if (showInviteGate.value)
        viewState.value = 'invite';
      else
        viewState.value = 'landing';
    }

    function selectInitialQuestion() {
      if (!evaluation.value.flowType) {
        viewState.value = 'landing'
        return
      }

      // Check track questions first — only those still worth asking (PD-1324b).
      if (askableQuestions.value.length > 0) {
        const unansweredTrackIndex = askableQuestions.value.findIndex(
          tq => !trackAnswers.value.some(a => a.questionNo === tq.no && a.selectedKey)
        )
        //allow the user to confirm document if they haven't answered any track questions yet
        if (unansweredTrackIndex === 0) {
          viewState.value = 'welcome'
          return
        }
        else if (unansweredTrackIndex > 0) {
          currentTrackIndex.value = unansweredTrackIndex
          viewState.value = 'track-question'
          return
        }
      }

      if (evaluation.value.flowType === 'classification') {
        if (evaluation.value.classificationZoneIndex === null) {
          viewState.value = 'classification-zone'
          return
        }
        const unansweredClassIndex = classificationFactors.value.findIndex(
          f => !classificationAnswers.value.some(a => a.factorName === f.name)
        )
        if (unansweredClassIndex >= 0) {
          currentClassificationIndex.value = unansweredClassIndex
          aiAssist.startClassification(() => aiSourceText.value, doc.value?.system_number)
          aiAssist.ensurePrefetched(unansweredClassIndex)
          viewState.value = 'classification-question'
        } else {
          convertClassificationToEvaluationAnswers()
          viewState.value = 'summary'
        }
      } else if (evaluation.value.flowType === 'guided') {
        // Rebuild visible questions from existing guided answers
        currentGuidedIndex.value = 0
        updateVisibleQuestions()
        const unansweredGuidedIndex = visibleQuestionIds.value.findIndex(
          qid => !guidedAnswers.value.some(a => a.questionId === qid)
        )
        if (unansweredGuidedIndex >= 0) {
          currentGuidedIndex.value = unansweredGuidedIndex
          viewState.value = 'guided-question'
        } else {
          convertGuidedToEvaluationAnswers()
          viewState.value = 'summary'
        }
      } else {
        // Standard flow
        for (let fi = 0; fi < factors.value.length; fi++) {
          const factor = factors.value[fi]
          for (let si = 0; si < factor.subFactors.length; si++) {
            const subFactor = factor.subFactors[si]
            const hasAnswer = answers.value.some(
              a => a.factorId === factor.id && a.subFactorId === subFactor.id
            )
            if (!hasAnswer) {
              currentFactorIndex.value = fi
              currentSubFactorIndex.value = si
              aiAssist.startClassification(() => aiSourceText.value, doc.value?.system_number)
              aiAssist.ensurePrefetched(currentGlobalSubFactorIndex.value)
              viewState.value = 'sub-factor'
              return
            }
          }
        }
        // All answered
        viewState.value = 'summary'
      }
    }

    async function loadEvaluationDataFromApi() {
      let possibleError = false;
      let data = {};
      await axios
        .post(`document/loadDocumentLfEvaluation`, {
          doc_id: doc.value?.doc_id,
          doc_reference: props.docReference,
          document_lf_evaluation_id: evaluation.value.id,
        })
        .then(async (resp) => {
          possibleError = true;
          if (resp.data.Status === "OK") {
            data = resp.data.Data;
          }
        })
        .catch((err) => {
          if (possibleError) {
            alert("Code Error");
          } else if (err.response && err.response.status === 401) {
            emit("sessionExpired", err);
          } else {
            alert(err.response ? err.response.data.message : err);
          }
          console.log(err);
        });

      return data;
    }

    function raiseError(message) {
      viewState.value = 'error'
      errorMessage.value = message
    }

    // The single place the JD source is switched — used by the initial upload
    // and by the replace/add controls shown during the evaluation. Re-points
    // contentSource, persists it, reloads the related-docs panel (syncing the
    // file toggle to the new file), and — when an assisted flow is already
    // underway — resets AI assist so its evidence is recomputed against the new
    // document. The AI cache is keyed by chain+unit only (no file dimension),
    // and the aiSourceText watcher only re-fires on empty→non-empty, so a
    // non-empty→non-empty source swap must reset AI assist explicitly.
    async function setJdSource(file) {
      contentSource.value.type = 'file'
      contentSource.value.file_id = file.file_id
      contentSource.value.row_id = file.row_id ?? null
      contentSource.value.file_name = file.file_name ?? null
      await saveEvaluation()
      if (file.content)
        contentSource.value.content = file.content
      else
        await fetchFileData()

      // Only (re)start AI assist when a flow that uses it is already in progress;
      // during the initial upload the flow isn't chosen yet and selectFlow starts
      // it. startClassification triggers executions, so guard against firing early.
      if (['classification', 'standard'].includes(evaluation.value.flowType)) {
        await nextTick()
        aiAssist.startClassification(() => aiSourceText.value, doc.value?.system_number)
        aiAssist.ensurePrefetched(aiCurrentUnitIndex.value || 0)
      }
    }

    async function fileUploaded(file) {
      await setJdSource(file)
      await completeUpload()
    }

    function uploadSkipped() {
      completeUpload()
    }

    async function completeUpload() {
      if (inviteResolved.value && inviteResolved.value.length > 0) {
        viewState.value = 'thankyou'
        thankYouTitle.value = 'Invite sent'
        thankYouMessageOverride.value = `${inviteResolved.value[0].user_name} has been invited to evaluate “${doc.value?.doc_name}”. You’ll be notified when it returns for your review.`
      } else {
        await enterTrackQuestions()
      }
    }

    async function fetchFileData() {
      if (!contentSource.value || !contentSource.value.file_id) return
      relatedContentLoading.value = true
      const sourceText = await nlpUtils.getExtractedFileText(contentSource.value.file_id, contentSource.value.row_id)
      contentSource.value.content = sourceText || "Text could not be extracted from this document"
      relatedContentLoading.value = false
    }

    async function selectFlow(flow) {
      // Anchor roles (PD-1258): anchors require a full, point-scored evaluation —
      // a classification can't be a calibration reference. The method cards are
      // disabled under the intent; this is the backstop.
      if (anchorIntent.value && flow !== 'standard') return
      evaluation.value.flowType = flow
      await saveEvaluation()

      trackAnswers.value = []
      currentTrackIndex.value = 0
      viewState.value = 'welcome'
    }

    // The "Is this an anchor role?" switch on the method screen. Turning it ON
    // drops a non-evaluation method, so the user re-picks rather than silently
    // carrying an incompatible pair.
    async function setAnchorIntent(on) {
      if (!canGovernAnchors.value) return
      evaluation.value.isAnchor = !!on
      // Dropping the anchor drops the layered CEO intent with it.
      if (!on) { evaluation.value.anchorRationale = null; evaluation.value.isCeiling = false }
      if (on && evaluation.value.flowType && evaluation.value.flowType !== 'standard') {
        evaluation.value.flowType = null
      }
      await saveEvaluation()
    }

    // "Previous" on the document step — the method is the only step behind it, and
    // the chosen flow stays selected on the landing page so it reads as a step back
    // rather than a reset.
    function handleWelcomeBack() {
      viewState.value = 'landing'
    }

    async function startSelectedFlow() {
      if (evaluation.value.flowType === 'classification' || evaluation.value.flowType === 'standard') {
        await nextTick()
        aiAssist.startClassification(() => aiSourceText.value, doc.value?.system_number)
      }

      if (evaluation.value.flowType === 'standard') {
        // Go straight to the first sub-factor (the factor-intro step was removed).
        currentFactorIndex.value = 0
        currentSubFactorIndex.value = 0
        aiAssist.ensurePrefetched(0)
        viewState.value = 'sub-factor'
      } else if (evaluation.value.flowType === 'classification') {
        evaluation.value.classificationZoneIndex = null
        classificationAnswers.value = []
        currentClassificationIndex.value = 0
        aiAssist.ensurePrefetched(0)
        viewState.value = 'classification-zone'
      } else {
        initGuidedFlow()
        viewState.value = 'guided-question'
      }
    }

    async function handleZoneAnswer(zoneIndex) {
      evaluation.value.classificationZoneIndex = zoneIndex
      await saveEvaluation()
      viewState.value = 'classification-question'
    }

    function handleZoneBack() {
      goBackBeforeFlow()
    }

    function initGuidedFlow() {
      guidedAnswers.value = []
      currentGuidedIndex.value = 0
      updateVisibleQuestions()
    }

    function updateVisibleQuestions() {
      const visible = []
      const answeredMap = new Map(guidedAnswers.value.map(a => [a.questionId, a]))

      for (const q of alternateQuestions.value) {
        if (shouldShowQuestion(q, answeredMap)) {
          visible.push(q.id)
        }
      }

      visibleQuestionIds.value = visible
    }

    function shouldShowQuestion(question, answeredMap) {
      const condition = question.showCondition.toLowerCase()

      if (condition === 'always' || condition.includes('always')) {
        return true
      }

      // Parse conditions like "If Q2 ∈ {D,E,F}" or "If Q8 = Yes"
      // Format: "If Q{n} = {value}" or "If Q{n} ∈ {values}"
      const ifMatch = condition.match(/if\s+q(\d+[a-z]?)\s*(=|∈|in|>=|≥)\s*\{?([^}]+)\}?/i)
      if (ifMatch && ifMatch[1] && ifMatch[2] && ifMatch[3]) {
        const questionId = `Q${ifMatch[1].toUpperCase()}`
        const operator = ifMatch[2]
        const values = ifMatch[3].split(',').map(v => v.trim().toUpperCase())

        const answer = answeredMap.get(questionId)
        if (!answer) return false

        const answerKey = answer.selectedKey.toUpperCase()

        if (operator === '=' || operator === '∈' || operator.toLowerCase() === 'in') {
          return values.includes(answerKey)
        }

        if (operator === '>=' || operator === '≥') {
          const threshold = values[0]
          if (threshold) {
            return answerKey >= threshold
          }
        }
      }

      // Check for "Show if (condition) OR (condition)" format
      const orConditions = condition.split(/\s+or\s+/i)
      if (orConditions.length > 1) {
        for (const orCond of orConditions) {
          const orMatch = orCond.match(/q(\d+[a-z]?)\s*(>=|≥|=)\s*([A-F])/i)
          if (orMatch && orMatch[1] && orMatch[2] && orMatch[3]) {
            const qid = `Q${orMatch[1].toUpperCase()}`
            const op = orMatch[2]
            const val = orMatch[3].toUpperCase()
            const ans = answeredMap.get(qid)

            if (ans) {
              const ansKey = ans.selectedKey.toUpperCase()
              if ((op === '=' && ansKey === val) || ((op === '>=' || op === '≥') && ansKey >= val)) {
                return true
              }
            }
          }
        }
        return false
      }

      return true
    }

    async function handleGuidedAnswer(response) {
      // Nothing changed — just navigate. The answer set is untouched, so the
      // visible-question list can't have shifted either.
      if (!response.dirty) {
        moveToNextGuidedQuestion()
        return
      }

      // Rebuilt explicitly so the component's dirty flag doesn't leak into the
      // stored answer or the API payload.
      const answer = {
        questionId: response.questionId,
        selectedKey: response.selectedKey,
        selectedText: response.selectedText,
        comments: response.comments,
      }

      const existingIndex = guidedAnswers.value.findIndex(a => a.questionId === answer.questionId)
      if (existingIndex >= 0) {
        guidedAnswers.value.splice(existingIndex, 1, answer)
      } else {
        guidedAnswers.value.push(answer)
      }

      await saveEvaluationGuidedAnswer(answer)

      updateVisibleQuestions()
      moveToNextGuidedQuestion()
    }

    async function saveEvaluationGuidedAnswer(answer) {
      if (readOnly.value)
        return

      const data = {
        ...answer,
        evaluationId: evaluation.value.id,
      }

      let possibleError = false;
      await axios
        .post(`document/saveDocumentLfEvaluationGuidedAnswer`, data)
        .then(async (resp) => {
          possibleError = true;
        })
        .catch((err) => {
          if (possibleError) {
            alert("Code Error");
          } else if (err.response && err.response.status === 401) {
            emit("sessionExpired", err);
          } else {
            alert(err.response ? err.response.data.message : err);
          }
          console.log(err);
        });
    }

    function moveToNextGuidedQuestion() {
      if (currentGuidedIndex.value < visibleQuestionIds.value.length - 1) {
        currentGuidedIndex.value++
        return
      }

      // All questions answered - convert to evaluation answers and show summary
      convertGuidedToEvaluationAnswers()
      viewState.value = 'summary'
    }

    function convertGuidedToEvaluationAnswers() {
      answers.value = []
      const answeredMap = new Map(guidedAnswers.value.map(a => [a.questionId, a]))

      for (const factor of factors.value) {
        for (const subFactor of factor.subFactors) {
          const mapping = subfactorLevelMappings.value.find(m => m.subFactorNo === subFactor.id)
          let level = 0
          let derivation = null

          if (mapping) {
            // Check levels from highest (5) to lowest (0), use first match
            for (let l = 5; l >= 0; l--) {
              const levelStr = mapping.levels[l]
              if (!levelStr) continue

              const result = evaluateLevelMapping(levelStr, answeredMap)
              if (result.matched) {
                level = l
                derivation = {
                  questionId: result.matchedQuestions.join(', '),
                  question: levelStr,
                  answerKey: result.matchedAnswers.join(', '),
                  answerText: result.matchedTexts.join(', ')
                }
                break
              }
            }
          }

          const levelData = subFactor.levels[level]
          answers.value.push({
            factorId: factor.id,
            factorName: factor.name,
            subFactorId: subFactor.id,
            subFactorName: subFactor.name,
            selectedLevel: level,
            levelHeading: levelData?.heading ?? `Level ${level}`,
            derivation
          })
        }
      }
    }

    function evaluateLevelMapping(mappingStr, answeredMap) {
      // Extract Q-answer patterns like Q2-A, Q8-No, Q6-A/B (alternatives)
      const patterns = [...mappingStr.matchAll(/Q(\d+[a-z]?)-([A-F](?:\/[A-F])*|Yes|No)/gi)]
      if (patterns.length === 0) return { matched: false }

      const matchedQuestions = []
      const matchedAnswers = []
      const matchedTexts = []

      // All extracted conditions must be satisfied (AND logic)
      for (const pattern of patterns) {
        const questionId = `Q${pattern[1].toUpperCase()}`
        // Split on "/" to support alternatives like Q6-A/B meaning A or B
        const acceptableAnswers = pattern[2].toUpperCase().split('/')
        const answer = answeredMap.get(questionId)

        if (!answer || !acceptableAnswers.includes(answer.selectedKey.toUpperCase())) {
          return { matched: false }
        }

        matchedQuestions.push(questionId)
        matchedAnswers.push(answer.selectedKey)
        matchedTexts.push(answer.selectedText)
      }

      return { matched: true, matchedQuestions, matchedAnswers, matchedTexts }
    }

    function goBackBeforeFlow() {
      if (askableQuestions.value.length > 0) {
        currentTrackIndex.value = askableQuestions.value.length - 1
        viewState.value = 'track-question'
      } else {
        // Nothing was asked about the track (a single-discipline framework, or one
        // track under the discipline), so the step before the levels is the document.
        viewState.value = 'welcome'
      }
    }

    function handleGuidedBack() {
      if (currentGuidedIndex.value === 0) {
        goBackBeforeFlow()
        return
      }
      currentGuidedIndex.value--
    }

    async function handleAnswer(response) {
      const factor = currentFactor.value
      const subFactor = currentSubFactor.value
      if (!factor || !subFactor) return

      // Nothing changed — just navigate. Re-saving an identical answer would only
      // stamp a new updated_by/date and, when editing from the summary, needlessly
      // discard the manual level adjustment.
      if (!response.dirty) {
        moveToNext()
        return
      }

      const existingIndex = answers.value.findIndex(
        a => a.factorId === factor.id && a.subFactorId === subFactor.id
      )

      const levelData = subFactor.levels[response.level]
      const answer = {
        factorId: factor.id,
        factorName: factor.name,
        subFactorId: subFactor.id,
        subFactorName: subFactor.name,
        selectedLevel: response.level,
        levelHeading: levelData?.heading ?? `Level ${response.level}`,
        comments: response.comments,
        rationaleProvenance: response.rationaleProvenance ?? null,
        updated_by: loginState.user.name,
      }

      if (existingIndex >= 0) {
        answers.value.splice(existingIndex, 1, answer)
      } else {
        answers.value.push(answer)
      }

      await saveEvaluationSubFactorLevel(answer)

      // Editing an answer from the summary can change the recommended level, so
      // discard any manual level adjustment and its justification — the summary
      // will fall back to (and re-persist) the freshly suggested level.
      if (editingFromSummary.value) {
        evaluation.value.selectedLevelId = null
        evaluation.value.comments = null
        await saveEvaluation()
      }

      moveToNext()
    }

    async function saveEvaluationSubFactorLevel(answer) {
      if (readOnly.value)
        return

      const data = {
        ...answer,
        evaluationId: evaluation.value.id,
      }

      let possibleError = false;
      await axios
        .post(`document/saveDocumentLfEvaluationSubFactor`, data)
        .then(async (resp) => {
          possibleError = true;
        })
        .catch((err) => {
          if (possibleError) {
            alert("Code Error");
          } else if (err.response && err.response.status === 401) {
            emit("sessionExpired", err);
          } else {
            alert(err.response ? err.response.data.message : err);
          }
          console.log(err);
        });
    }

    function moveToNext() {
      const factor = currentFactor.value
      if (!factor) return

      // Editing a single sub-factor from the summary: save and return there
      // instead of continuing through the rest of the questionnaire.
      if (editingFromSummary.value) {
        editingFromSummary.value = false
        viewState.value = 'summary'
        return
      }

      if (currentSubFactorIndex.value < factor.subFactors.length - 1) {
        currentSubFactorIndex.value++
        return
      }

      if (currentFactorIndex.value < factors.value.length - 1) {
        // Advance straight into the next factor's first sub-factor (no factor-intro step).
        currentFactorIndex.value++
        currentSubFactorIndex.value = 0
        viewState.value = 'sub-factor'
        return
      }

      viewState.value = 'summary'
    }

    function handleBack() {
      // When editing a single sub-factor from the summary, going back returns
      // straight to the summary rather than walking the questionnaire.
      if (editingFromSummary.value) {
        editingFromSummary.value = false
        viewState.value = 'summary'
        return
      }
      if (currentSubFactorIndex.value === 0) {
        // First sub-factor of the first factor → back out of the flow; otherwise
        // step back to the previous factor's last sub-factor (no factor-intro step).
        if (currentFactorIndex.value === 0) {
          goBackBeforeFlow()
          return
        }
        currentFactorIndex.value--
        const prevFactor = factors.value[currentFactorIndex.value]
        currentSubFactorIndex.value = prevFactor ? prevFactor.subFactors.length - 1 : 0
        return
      }
      currentSubFactorIndex.value--
    }

    // Jump straight to a specific sub-factor question from the summary so the
    // user can amend its answer/justification, then return to the summary.
    function editSubFactor({ factorId, subFactorId }) {
      const fi = factors.value.findIndex(f => f.id === factorId)
      if (fi < 0) return
      const si = factors.value[fi].subFactors.findIndex(sf => sf.id === subFactorId)
      if (si < 0) return

      currentFactorIndex.value = fi
      currentSubFactorIndex.value = si
      editingFromSummary.value = true
      viewState.value = 'sub-factor'
    }

    // Cancel an in-place sub-factor edit: discard and return to the summary.
    function cancelSubFactorEdit() {
      editingFromSummary.value = false
      viewState.value = 'summary'
    }

    async function saveEvaluation() {
      if (readOnly.value)
        return

      const functionId = trackAnswers.value.find(ta => ta.questionNo === 1)?.selectedKey ?? null;
      const trackId = trackAnswers.value.find(ta => ta.questionNo === 2)?.selectedKey ?? null;
      const data = {
        evaluationId: evaluation.value.id,
        doc_id: doc.value.doc_id,
        flowType: evaluation.value.flowType,
        functionId: functionId,
        trackId: trackId,
        classificationZoneIndex: evaluation.value.classificationZoneIndex,
        score: evaluation.value?.score ?? null,
        suggestedLevelId: evaluation.value?.suggestedLevelId ?? null,
        selectedLevelId: evaluation.value?.selectedLevelId ?? null,
        comments: evaluation.value?.comments ?? null,
        status: evaluation.value?.status ?? 'DRAFT',
        file_id: contentSource.value.type === 'file' ? contentSource.value.file_id : null,
        row_id: contentSource.value.type === 'file' ? contentSource.value.row_id : null,
        // Anchor roles (PD-1258): the server applies these to document_lf_anchor
        // only when the evaluation itself completes as a full (STANDARD) one.
        // Sent as undefined while the feature is off so the payload is unchanged.
        isAnchor: anchorsEnabled.value ? !!evaluation.value.isAnchor : undefined,
        anchorRationale: anchorsEnabled.value ? (evaluation.value.anchorRationale || null) : undefined,
        isCeiling: anchorsEnabled.value && evaluation.value.isCeiling ? true : undefined,
      }

      processing.value = true
      let possibleError = false;
      await axios
        .post(`document/saveDocumentLfEvaluation`, data)
        .then(async (resp) => {
          possibleError = true;
          if (resp.data.Status === "OK") {
            evaluation.value.id = resp.data.Data.evaluationId
          }
          processing.value = false
        })
        .catch((err) => {
          processing.value = false
          if (possibleError) {
            alert("Code Error");
          } else if (err.response && err.response.status === 401) {
            emit("sessionExpired", err);
          } else {
            alert(err.response ? err.response.data.message : err);
          }
          console.log(err);
        });
    }

    async function handleTrackAnswer(answer) {
      const existingIndex = trackAnswers.value.findIndex(a => a.questionNo === answer.questionNo)
      if (existingIndex >= 0) {
        trackAnswers.value.splice(existingIndex, 1, answer)
      } else {
        trackAnswers.value.push(answer)
      }

      // Changing the discipline can strand a track answered under the old one —
      // the pairing would have no levels. Drop it so the track question is asked
      // again (and never saved) rather than carrying an unlevellable pairing on.
      if (answer.questionNo === 1) {
        const trackIndex = trackAnswers.value.findIndex(a => a.questionNo === 2)
        if (trackIndex >= 0 && trackStrandedByFunction(
          tracks.value, functionTrackLevels.value, answer.selectedKey,
          trackAnswers.value[trackIndex].selectedKey)) {
          trackAnswers.value.splice(trackIndex, 1)
        }
        // The discipline just chosen may level exactly one track, which settles the
        // next question and drops it from askableQuestions — so the branch below
        // hands straight over to the levels instead of asking it.
        applySettledTrackAnswers()
      }

      await saveEvaluation()

      // Editing the track from the summary: changing the track shifts the level
      // mapping, so discard any manual level adjustment/justification and return
      // straight to the summary (sub-factor answers are unaffected).
      if (editingTrackFromSummary.value) {
        editingTrackFromSummary.value = false
        evaluation.value.selectedLevelId = null
        evaluation.value.comments = null
        await saveEvaluation()
        viewState.value = 'summary'
        return
      }

      if (currentTrackIndex.value < askableQuestions.value.length - 1) {
        currentTrackIndex.value++
      } else {
        startSelectedFlow()
      }
    }

    async function startEvaluation(inviteResponse) {
      if (inviteResponse) {
        inviteResolved.value = inviteResponse
      }
      await initialize()
      viewState.value = 'landing'
    }

    // The first track question has no Previous — the document and method are
    // settled once the evaluation has started, so there is nothing to go back to
    // (Restart is the way out).
    function handleTrackBack() {
      if (currentTrackIndex.value === 0) return
      currentTrackIndex.value--
    }

    // Jump straight to the track selection question from the summary so the user
    // can change the track, then return to the summary. Standard flow only — the
    // sub-factor answers are unaffected, only the level mapping changes.
    function editTrack() {
      const ti = askableQuestions.value.findIndex(q => q.no === 2)
      if (ti < 0) return
      currentTrackIndex.value = ti
      editingTrackFromSummary.value = true
      viewState.value = 'track-question'
    }

    // "Review answers instead" from the summary's out-of-range prompt: jump back
    // to the first level question and walk the flow normally (Next/Previous),
    // ending at the summary again. Answers are preserved — this is a review, not
    // a restart — so it mirrors the resume paths rather than re-initialising.
    function reviewAnswers() {
      if (evaluation.value.flowType === 'guided') {
        currentGuidedIndex.value = 0
        updateVisibleQuestions()
        viewState.value = 'guided-question'
      } else {
        currentFactorIndex.value = 0
        currentSubFactorIndex.value = 0
        aiAssist.ensurePrefetched(0)
        viewState.value = 'sub-factor'
      }
    }

    // Cancel an in-place track edit: discard and return to the summary.
    function cancelTrackEdit() {
      editingTrackFromSummary.value = false
      viewState.value = 'summary'
    }

    function setTrackAnswer(questionNo, selectedKey) {
      const existingIndex = trackAnswers.value.findIndex(a => a.questionNo === questionNo)
      const base = existingIndex >= 0 ? trackAnswers.value[existingIndex] : {}
      const answer = { ...base, questionNo, selectedKey }
      if (existingIndex >= 0) {
        trackAnswers.value.splice(existingIndex, 1, answer)
      } else {
        trackAnswers.value.push(answer)
      }
    }

    // Switch directly to a suggested track from the summary's out-of-range
    // warning. Updates the track answer and persists; the summary re-derives the
    // recommended level (and re-saves it) reactively.
    async function changeTrack(trackId) {
      setTrackAnswer(2, trackId)
      await saveEvaluation()
    }

    // Switch discipline (track question 1) from the summary's out-of-range
    // warning, used when no track within the current discipline covers the score.
    // The discipline determines which score bands apply, and the suggested one may
    // only fit on a different career track, so both answers move together.
    async function changeFunction({ functionId, trackId }) {
      setTrackAnswer(1, functionId)
      if (trackId) setTrackAnswer(2, trackId)
      await saveEvaluation()
    }

    function handleLandingBack() {
      viewState.value = 'add-document'
    }

    async function handleClassificationAnswer(response) {
      const factor = currentClassificationFactor.value
      if (!factor) return

      // Nothing changed — just navigate, leaving the stored answer and any manual
      // level adjustment on the summary untouched.
      if (!response.dirty) {
        moveToNextClassification()
        return
      }

      const existingIndex = classificationAnswers.value.findIndex(a => a.factorId === factor.id)
      const level = levels.value.find(l => l.name === response.selectedLevel.toString())
      const answer = {
        factorId: factor.id, factorName: factor.name, selectedLevelId: level?.id,
        selectedLevel: response.selectedLevel, comments: response.comments,
        rationaleProvenance: response.rationaleProvenance ?? null,
        updated_by: loginState.user.name
      }

      if (existingIndex >= 0) {
        classificationAnswers.value.splice(existingIndex, 1, answer)
      } else {
        classificationAnswers.value.push(answer)
      }

      await saveEvaluationFactorLevel(answer)

      // Editing a single factor from the summary: the recommended level may now
      // differ, so discard any manual level adjustment/justification and refresh
      // the derived answers before returning to the summary.
      if (editingFromSummary.value) {
        evaluation.value.selectedLevelId = null
        evaluation.value.comments = null
        await saveEvaluation()
        convertClassificationToEvaluationAnswers()
      }

      moveToNextClassification()
    }

    function moveToNextClassification() {
      // Editing a single factor from the summary returns straight there rather
      // than continuing through the rest of the factors.
      if (editingFromSummary.value) {
        editingFromSummary.value = false
        viewState.value = 'summary'
        return
      }

      if (currentClassificationIndex.value < classificationFactors.value.length - 1) {
        currentClassificationIndex.value++
        return
      }

      convertClassificationToEvaluationAnswers()
      viewState.value = 'summary'
    }

    async function saveEvaluationFactorLevel(answer) {
      if (readOnly.value)
        return

      const data = {
        ...answer,
        evaluationId: evaluation.value.id
      }

      let possibleError = false;
      await axios
        .post(`document/saveDocumentLfEvaluationFactor`, data)
        .then(async (resp) => {
          possibleError = true;
        })
        .catch((err) => {
          if (possibleError) {
            alert("Code Error");
          } else if (err.response && err.response.status === 401) {
            emit("sessionExpired", err);
          } else {
            alert(err.response ? err.response.data.message : err);
          }
          console.log(err);
        });
    }

    function handleClassificationBack() {
      // When editing a single factor from the summary, going back returns
      // straight to the summary rather than walking the questionnaire.
      if (editingFromSummary.value) {
        editingFromSummary.value = false
        viewState.value = 'summary'
        return
      }
      if (currentClassificationIndex.value === 0) {
        viewState.value = 'classification-zone'
        return
      }
      currentClassificationIndex.value--
    }

    // Jump straight to a specific classification factor question from the summary
    // so the user can amend its answer/rationale, then return to the summary.
    function editClassificationFactor({ factorId }) {
      const fi = factors.value.findIndex(f => f.id === factorId)
      if (fi < 0) return

      currentClassificationIndex.value = fi
      editingFromSummary.value = true
      aiAssist.startClassification(() => aiSourceText.value, doc.value?.system_number)
      aiAssist.ensurePrefetched(fi)
      viewState.value = 'classification-question'
    }

    // Cancel an in-place classification factor edit: discard and return to the summary.
    function cancelClassificationEdit() {
      editingFromSummary.value = false
      viewState.value = 'summary'
    }

    function convertClassificationToEvaluationAnswers() {
      answers.value = []
      for (const factor of factors.value) {
        for (const subFactor of factor.subFactors) {
          const classAnswer = classificationAnswers.value.find(a => a.factorName === factor.name)
          const level = classAnswer ? classAnswer.selectedLevel : 0
          const levelData = subFactor.levels ? subFactor.levels[level] : null
          answers.value.push({
            factorId: factor.id,
            factorName: factor.name,
            subFactorId: subFactor.id,
            subFactorName: subFactor.name,
            selectedLevel: level,
            levelHeading: levelData?.heading ?? `Level ${level}`
          })
        }
      }
    }

    // Which Compare tab to land on when opening the comparison view from the
    // summary toggle: 'compare' (default) or 'breakdown' for Factor Breakdown.
    const comparisonInitialTab = ref('compare')
    function openComparison(tab) {
      comparisonInitialTab.value = tab === 'breakdown' ? 'breakdown' : 'compare'
      viewState.value = 'comparison'
    }

    function summaryUpdated(summary) {
      if (!summary || !evaluation.value.id) return
      evaluation.value.score = summary.score;
      evaluation.value.suggestedLevelId = summary.suggestedLevelId;
      evaluation.value.selectedLevelId = summary.selectedLevelId;
      evaluation.value.comments = summary.comments || null;
      // Anchor roles (PD-1258): the summary's "Save as an anchor role" tick +
      // rationale. Only honoured while the feature is on and the user may govern.
      if (anchorsEnabled.value && canGovernAnchors.value && summary.isAnchor !== undefined) {
        evaluation.value.isAnchor = !!summary.isAnchor;
        evaluation.value.anchorRationale = summary.anchorRationale || null;
      }
      saveEvaluation();
    }

    async function complete() {
      //a viewer cannot save, so cannot complete — the summary hides the control too
      if (readOnly.value || !evaluation.value.id) return
      evaluation.value.status = 'COMPLETED'
      await saveEvaluation()
        .then(() => {
          const doc_id = doc.value?.doc_id;
          const action = editAction.value;

          if (!doc_id || !action) return;

          let request = {
            doc_id: doc_id,
            action_available_id: action.action_available_id,
            lifecycle_role_id: action.lifecycle_role_id,
            document_role_id: action.document_role_id,
            preProcessResponse: { evaluationId: evaluation.value.id },
            returnHeaderOnly: true,
          };
          
          processing.value = true
          return axios
            .post("document/processdocumentaction/", request)
            .then((resp) => {
              let result = { redirectURL: "" };
              if (resp.data.Status === "OK") {
                if (resp.data.Data.stateChanged) {
                  if (
                    resp.data.Data.documents &&
                    resp.data.Data.documents.length
                  ) {
                    store.dispatch(
                      "docs/updateDoc",
                      resp.data.Data.documents[0]
                    );
                  } else {
                    //state changed but no doc - probably don't have permission to view the document any more.
                    store.dispatch("docs/removeDoc", request.doc_id);
                  }
                  store.dispatch("tasks/refresh", request.doc_id);
                }
                if (resp.data.Data.redirectURL) {
                  result.redirectURL = resp.data.Data.redirectURL;
                }
              } else {
                console.log(resp.data.Message);
              }
              processing.value = false
            })
            .catch((err) => {
              processing.value = false
              if (err.response && err.response.status === 401) {
                emit("sessionExpired", err);
              } else {
                console.log(err);
                alert(err.response ? err.response.data.message : err);
              }
            });
        });
      viewState.value = 'thankyou'
      thankYouTitle.value = 'Thank you'
      //the message follows the flow — see completionMessage
      thankYouMessageOverride.value = null
    }

    function finish() {
      router.go(-1)
    }

    function promptRestart() {
      showRestartConfirm.value = true
    }

    /* "Evaluate to anchor" from a CLASSIFICATION summary (PD-1258). Straight into the
       restart, as the prototype does — no confirmation step. The intent is set AFTER
       the restart: restart() resets the evaluation, so setting it first would be wiped.
       Turning it on also locks the method to a full evaluation. */
    async function restartAsAnchor() {
      await restart()
      evaluation.value.isAnchor = true
      /* …and then PAST the method step. restart() lands on Method, but under the anchor
         intent it has nothing to offer — Evaluate is the only permitted route and
         Classify renders locked — so stopping there is a dead click. Selecting the flow
         carries on to the re-evaluate/Document step, which is where the anchor note is
         stated.
         The load-time path now does the same thing by a different route: an existing
         non-standard evaluation normalises to 'standard' and sets `anchorNeedsRelevel`,
         so arriving with ?anchor=1 on an already-levelled role also enters at
         re-evaluate rather than on Method. Method is still the entry for a role with NO
         evaluation, which is the only case left where it is a genuine starting choice. */
      await selectFlow('standard')
    }

    async function confirmRestart() {
      showRestartConfirm.value = false
      await restart()
    }

    function promptDelete() {
      showDeleteConfirm.value = true
    }

    async function confirmDelete() {
      const action = deleteAction.value
      if (!evaluation.value.id || !action) {
        showDeleteConfirm.value = false
        return
      }

      let request = {
        doc_id: doc.value.doc_id,
        action_available_id: action.action_available_id,
        lifecycle_role_id: action.lifecycle_role_id,
        document_role_id: action.document_role_id,
        preProcessResponse: { evaluationId: evaluation.value.id, delete: true },
        returnHeaderOnly: true,
      };

      deleting.value = true
      let possibleError = false
      await axios
        .post("document/processdocumentaction/", request)
        .then((resp) => {
          possibleError = true
          if (resp.data.Status === "OK") {
            showDeleteConfirm.value = false
            finish()
          } else {
            alert(resp.data.Message ?? "Unable to delete the evaluation")
          }
          deleting.value = false
        })
        .catch((err) => {
          deleting.value = false
          if (possibleError) {
            alert("Code Error")
          } else if (err.response && err.response.status === 401) {
            emit("sessionExpired", err)
          } else {
            alert(err.response ? err.response.data.message : err)
          }
          console.log(err)
        })
    }

    async function restart() {
      evaluation.value.id = null
      evaluation.value.flowType = null
      evaluation.value.status = 'DRAFT'
      evaluation.value.score = null
      evaluation.value.comments = null
      evaluation.value.suggestedLevelId = null
      evaluation.value.selectedLevelId = null
      currentFactorIndex.value = 0
      currentSubFactorIndex.value = 0
      editingFromSummary.value = false
      editingTrackFromSummary.value = false
      answers.value = []
      guidedAnswers.value = []
      currentGuidedIndex.value = 0
      classificationAnswers.value = []
      currentClassificationIndex.value = 0
      trackAnswers.value = []
      currentTrackIndex.value = 0
      contentSource.value.type = 'document'
      contentSource.value.file_id = null
      contentSource.value.row_id = null
      contentSource.value.file_name = null
      // Start over from the method step (now the first step); re-arm the invite fork.
      pendingFlowType.value = null
      inviteResolved.value = null
      if (showInviteGate.value)
        viewState.value = 'invite';
      else
        viewState.value = 'landing';
      showRestartConfirm.value = false
      await saveEvaluation();
    }

    const DEFAULT_AI_CHAIN_IDS = {
      classification: 11,
      evaluation: 17,
      alignment_score_min: 4,
      centrality_score_min: 4,
      prefetch_ahead: 2,
      readiness: null,
      readiness_green_min: 0.6,
      // AI rationale drafting (PD-1301). Off until the tenant sets the chain id.
      rationale_draft: 21,
      rationale_evidence_top_k: 5,
    }
    const aiChainIds = computed(() => {
      const raw = store.state.settings.config?.settings
        ?.find(s => s.setting === 'ai_assist_chain_ids')?.value
      let parsed = {}
      try { parsed = raw ? JSON.parse(raw) : {} } catch { parsed = {} }
      return { ...DEFAULT_AI_CHAIN_IDS, ...parsed }
    })

    // JD readiness check (PD-1250) — advisory doc-content check shown on the
    // Welcome "Check the document" screens. Off unless the client has set a
    // `readiness` chain id in the ai_assist_chain_ids setting.
    const readinessChainId = computed(() => aiChainIds.value.readiness || null)
    const readinessThresholds = computed(() => ({ green_min: aiChainIds.value.readiness_green_min }))

    const isClassificationFlow = computed(() => evaluation.value.flowType === 'classification')

    const aiChainId = computed(() =>
      isClassificationFlow.value ? aiChainIds.value.classification : aiChainIds.value.evaluation
    )

    // AI rationale drafting (PD-1301) — what the question's "Use Atlas" needs for
    // the unit on screen. Enabled only when BOTH the rationale_draft chain and the
    // journey's assist chain are configured: the draft is grounded on the
    // assist's evidence, so without it there is nothing to draft from (PD-1246).
    // The product switch: `levelling_ai_rationale` client setting (BOOL, off by
    // default — same shape as levelling_anchor_roles). The chain ids below are
    // the plumbing it also needs.
    const aiRationaleEnabled = computed(() => {
      const raw = store.state.settings.config?.settings
        ?.find(s => s.setting === 'levelling_ai_rationale')?.value
      return ['true', '1'].includes(String(raw ?? '').trim().toLowerCase())
    })
    const rationaleDraftContext = computed(() => ({
      enabled: aiRationaleEnabled.value && !!aiChainIds.value.rationale_draft && !!aiChainId.value,
      chainId: aiChainIds.value.rationale_draft || null,
      topK: aiChainIds.value.rationale_evidence_top_k,
      systemReference: doc.value?.system_number || null,
      getEvidence: () => aiAssist.rawEvidenceFor(aiCurrentUnitName.value),
      ensureEvidence: () => aiAssist.ensureUnitTriggered(aiCurrentUnitName.value),
    }))

    const aiUnits = computed(() => {
      if (isClassificationFlow.value) {
        return classificationFactors.value.map(f => ({
          key: f.name,
          factorName: f.name,
          subFactors: (f.subFactors || []).map(sf => ({ name: sf.name, description: sf.description })),
        }))
      }
      const units = []
      for (const f of factors.value) {
        for (const sf of (f.subFactors || [])) {
          units.push({
            key: `${f.name} :: ${sf.name}`,
            factorName: f.name,
            subFactors: [{ name: sf.name, description: sf.description }],
          })
        }
      }
      return units
    })

    const aiCurrentUnitIndex = computed(() =>
      isClassificationFlow.value ? currentClassificationIndex.value : currentGlobalSubFactorIndex.value
    )

    const aiCurrentUnitName = computed(() => {
      if (isClassificationFlow.value) return currentClassificationFactorName.value
      const f = currentFactor.value
      const sf = currentSubFactor.value
      return (f && sf) ? `${f.name} :: ${sf.name}` : null
    })

    aiAssist.connectToView({
      units: aiUnits,
      currentUnitName: aiCurrentUnitName,
      currentUnitIndex: aiCurrentUnitIndex,
      viewState,
      chainId: aiChainId,
      filterMode: computed(() => isClassificationFlow.value ? 'classification' : 'evaluation'),
      alignmentScoreMin: computed(() => aiChainIds.value.alignment_score_min),
      centralityScoreMin: computed(() => aiChainIds.value.centrality_score_min),
      prefetchAhead: computed(() => aiChainIds.value.prefetch_ahead),
      panelViewStates: ['classification-question', 'sub-factor'],
      toggleViewStates: ['classification-zone', 'classification-question', 'sub-factor'],
      keepStates: ['classification-question', 'classification-zone', 'track-question', 'sub-factor'],
    })

    return {
      loginState,
      welcomeSubState,
      welcomePreview,
      showDocDetails,
      evaluation,
      readOnly,
      processing,
      doc,
      task,
      inviteResolved,
      relatedContentLoading,
      openSummaryEvent,
      evaluationData,
      levels,
      functions,
      tracks,
      factors,
      classificationFactors,
      functionTrackLevels,
      alternateQuestions,
      trackQuestions,
      askableQuestions,
      currentFactorIndex,
      currentSubFactorIndex,
      answers,
      guidedAnswers,
      visibleQuestionIds,
      currentGuidedIndex,
      trackAnswers,
      currentTrackIndex,
      selectedFunction,
      selectedTrack,
      selectedLevelAbbreviation,
      viewState,
      raiseError,
      errorMessage,
      hasCompletedEvaluation,
      currentFactor,
      currentSubFactor,
      totalSubFactors,
      currentGlobalSubFactorIndex,
      factorSteps,
      goToFactor,
      flowSteps,
      showFlowStepper,
      currentStepLabel,
      goToFlowStep,
      previousAnswer,
      currentGuidedQuestion,
      previousGuidedAnswer,
      currentTrackQuestion,
      previousTrackAnswer,
      uploadSkipped,
      fileUploaded,
      startEvaluation,
      selectFlow,
      handleWelcomeBack,
      handleAnswer,
      handleBack,
      editingFromSummary,
      editSubFactor,
      cancelSubFactorEdit,
      handleGuidedAnswer,
      handleGuidedBack,
      handleTrackAnswer,
      handleTrackBack,
      editingTrackFromSummary,
      editTrack,
      cancelTrackEdit,
      changeTrack,
      changeFunction,
      reviewAnswers,
      handleLandingBack,
      classificationAnswers,
      currentClassificationIndex,
      currentClassificationFactor,
      classificationLevelsForCurrentFactor,
      allClassificationLevelsForCurrentFactor,
      previousClassificationAnswer,
      handleClassificationAnswer,
      handleClassificationBack,
      editClassificationFactor,
      cancelClassificationEdit,
      classificationZones,
      handleZoneAnswer,
      handleZoneBack,
      // Anchor roles (PD-1258)
      anchorsRaw,
      anchorsEnabled,
      canGovernAnchors,
      anchorIntent,
      ceiling,
      ceilingAppliesToTrack,
      ceilingNote,
      zoneSuggestion,
      setAnchorIntent,
      contentSource,
      guidanceText,
      aiAssist,
      aiSourceText,
      jdPlainText,
      readinessChainId,
      readinessThresholds,
      rationaleDraftContext,
      summaryUpdated,
      comparisonInitialTab,
      openComparison,
      complete,
      finish,
      promptRestart,
      restartAsAnchor,
      anchors,
      confirmRestart,
      showRestartConfirm,
      anchorFamily,
      promptDelete,
      confirmDelete,
      showDeleteConfirm,
      deleting,
      deleteAction,
      showGuidance,
      showActivity,
      openActivity,
      activityClosed,
      docDetailsExpanded,
      toggleDocDetailsExpanded,
      docHeaderTitle,
      docHeaderSubtitle,
      thankYouTitle,
      thankYouMessage,
    }
  }
})

</script>

<template>
  <v-container fluid class="main-background pb-6 lvl-eval" :class="[ action ? '' : 'eval-docked', `state-${viewState}`, welcomeSubState ? `sub-${welcomeSubState}` : '', showDocDetails ? 'show-doc-details' : 'hide-doc-details']">
    <input type="hidden" id="document_lf_evaluation_id" :value="evaluation?.id" />
    <input type="hidden" id="document_lf_evaluation_document_reference" :value="doc?.system_number" />
    <div class="evaluation-container">
      <v-row class="evaluation-nav">
        <v-col
          v-if="(viewState === 'comparison' || !action) || showFlowStepper || (doc && !action && viewState !== 'landing')"
          cols="12"
          class="links-col"
        >
          <div class="eval-topbar">
            <div class="eval-topbar-left">
              <template v-if="!action">
                <button type="button" class="lvl-backchip" @click="finish" title="Back">
                  <span class="ms">close</span><span>Close</span>
                </button>
              </template>
            </div>

            <div v-if="showFlowStepper" class="eval-topbar-center">
              <div class="lvl-flowsteps" aria-label="Evaluation progress">
                <div
                  v-for="(step, i) in flowSteps"
                  :key="i"
                  class="lvl-flowstep-item"
                  :class="{ 'is-last': i === flowSteps.length - 1 }"
                >
                  <button
                    type="button"
                    class="lvl-flowstep"
                    :class="[step.state, { 'is-clickable': step.clickable }]"
                    :disabled="!step.clickable"
                    :aria-current="step.state === 'current' ? 'step' : undefined"
                    @click="goToFlowStep(i)"
                  >
                    <span class="lvl-flowstep-num">
                      <span v-if="step.state === 'done'" class="ms" style="font-size:15px">check</span>
                      <template v-else>{{ i + 1 }}</template>
                    </span>
                    <span class="lvl-flowstep-label">{{ step.label }}</span>
                  </button>
                  <span v-if="i < flowSteps.length - 1" class="lvl-flowstep-line" :class="{ done: step.state === 'done' }"></span>
                </div>
              </div>
            </div>
            <div v-else-if="evaluation.status !== 'DRAFT'" class="eval-topbar-center">
              <div class="lvl-wiz-title">
                <span class="k">Levelling record</span>
                <a @click.stop="$emit('openDocument', doc)" class="t" data-testid="approved-open-document-link">{{ docHeaderTitle }}</a>
              </div>
            </div>

            <div class="eval-topbar-right">
              <div
                v-if="evaluation.status === 'DRAFT' && doc && !action"
                class="lvl-wiz-ref"
                :title="doc.doc_name"
              >
                <span class="ms">description</span>
                <a @click.stop="$emit('openDocument', doc)" data-testid="open-document-link">{{ doc.doc_name }}</a>
              </div>
              <div
                v-else-if="selectedLevelAbbreviation"
                class="lvl-wiz-statpill"
              >
                <span class="ms">verified</span>
                Selected Level {{ selectedLevelAbbreviation }}
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
      <v-row class="evaluation-row">
        <v-col :cols="showDocDetails ? (docDetailsExpanded ? 5 : 7) : 12">
          <template v-if="viewState === 'landing'">
            <!-- Landing Page -->
            <LandingPage
              :previous-answer="evaluation.flowType"
              :can-anchor="canGovernAnchors"
              :is-anchor="anchorIntent"
              @selectFlow="selectFlow"
              @back="handleLandingBack"
              @update:isAnchor="setAnchorIntent"
            />
          </template>
          <v-card v-else :class="action ? 'evaluation-holder' : 'evaluation-card'">
            <v-card-title
              @click.prevent="$emit('toggleHide')"
              v-if="action && action.isDataCaptureEditPopup"
            >
              <v-spacer></v-spacer>
              <!-- Audit history (summary only) -->
              <button
                v-if="viewState === 'summary' && doc"
                type="button"
                class="lvl-iconbtn"
                @click.stop="openActivity"
                title="Audit History"
              >
                <span class="ms">track_changes</span>
              </button>

              <!-- Help icon -->
              <!-- <button
                v-if="!action"
                type="button"
                class="lvl-iconbtn"
                :class="{ 'is-on': showGuidance }"
                title="Guidance"
                @click.stop="showGuidance = !showGuidance"
              >
                <span class="ms" :class="{ fill: showGuidance }">help</span>
              </button> -->
              <button
                type="button"
                class="lvl-iconbtn ml-2"
                @click.stop="$emit('close')"
                title="Close"
              >
                <span class="ms">close</span>
              </button>
            </v-card-title>
            <v-expand-transition>
              <div v-if="!action && showGuidance" class="guidance-panel">
                {{ guidanceText }}
              </div>
            </v-expand-transition>
            <v-card-text>
              <!-- Loading State -->
              <v-container v-if="viewState === 'loading' || processing" class="fill-height">
                <v-row justify="center" align="center">
                  <v-col cols="auto" class="text-center">
                    <v-progress-circular
                      indeterminate
                      color="primary"
                      size="64"
                    ></v-progress-circular>
                    <p class="mt-4 text-h6">Loading...</p>
                  </v-col>
                </v-row>
              </v-container>

              <!-- Error State -->
              <v-container v-else-if="viewState === 'error'" class="fill-height">
                <v-row justify="center" align="center" class="mx-6">
                  <v-col cols="12">
                    <v-alert type="error" text>
                      <div class="subtitle-1 font-weight-bold mb-2">Error</div>
                      {{ errorMessage }}
                    </v-alert>
                  </v-col>
                </v-row>
              </v-container>

              <EvaluationInvite
                v-else-if="viewState === 'invite'"
                :doc="doc"
                title="Set the Level for This Role"
                subTitle=""
                :frameLess="true"
                :anchorIntent="anchorIntent"
                :canGovernAnchors="canGovernAnchors"
                :anchorFamily="anchorFamily"
                @start="startEvaluation"
                @closed="finish"
                @dropAnchorIntent="setAnchorIntent(false)"
              />

              <Welcome
                v-else-if="viewState === 'welcome'"
                :doc="doc"
                :task="task ?? undefined"
                :isNew="isNew"
                :has-previous-evaluation="hasCompletedEvaluation"
                :factors="factors"
                :jd-system-text="jdPlainText"
                :readiness-chain-id="readinessChainId"
                :readiness-thresholds="readinessThresholds"
                :confirmMessage="inviteResolved ? 'Send invitation' : 'Continue'"
                :anchor-intent="!!evaluation.isAnchor"
                :is-anchor="anchorsEnabled && anchors.isAnchor(doc?.doc_id)"
                :anchor-pending="!!(anchors.anchorInfo(doc?.doc_id) || {}).pending"
                :anchor-family="anchorFamily"
                @viewState="welcomeSubState = $event"
                @preview="welcomePreview = $event"
                @uploadSkipped="uploadSkipped"
                @fileUploaded="fileUploaded"
                @back="handleWelcomeBack">
              </Welcome>

              <!-- Track Questions -->
              <TrackQuestion
                v-else-if="viewState === 'track-question' && currentTrackQuestion"
                :readOnly="readOnly"
                :canDelete="!!deleteAction"
                :question="currentTrackQuestion"
                :current-index="currentTrackIndex"
                :total-questions="askableQuestions.length"
                :previous-answer="previousTrackAnswer"
                :editing="editingTrackFromSummary"
                @answer="handleTrackAnswer"
                @back="handleTrackBack"
                @cancel="cancelTrackEdit"
                @restart="promptRestart"
                @delete="promptDelete"
              />

              <!-- Standard Flow: Sub-factor Question -->
              <SubFactorQuestion
                v-else-if="viewState === 'sub-factor' && currentFactor && currentSubFactor"
                :readOnly="readOnly"
                :canDelete="!!deleteAction"
                :sub-factor="currentSubFactor"
                :factor="currentFactor"
                :current-sub-factor-index="currentGlobalSubFactorIndex"
                :total-sub-factors="totalSubFactors"
                :previous-answer="previousAnswer"
                :editing="editingFromSummary"
                :steps="factorSteps"
                :rationale-draft="rationaleDraftContext"
                @answer="handleAnswer"
                @back="handleBack"
                @cancel="cancelSubFactorEdit"
                @restart="promptRestart"
                @delete="promptDelete"
                @jumpToFactor="goToFactor"
              />

              <!-- Classification Flow: Zone Selection -->
              <ZoneQuestion
                v-else-if="viewState === 'classification-zone'"
                :readOnly="readOnly"
                :canDelete="!!deleteAction"
                :zones="classificationZones"
                :previous-answer="evaluation.classificationZoneIndex"
                :ceiling-note="ceilingNote"
                :suggestion="zoneSuggestion"
                @answer="handleZoneAnswer"
                @back="handleZoneBack"
                @restart="promptRestart"
                @delete="promptDelete"
              />

              <!-- Classification Flow: Factor Question -->
              <ClassificationQuestion
                v-else-if="viewState === 'classification-question' && currentClassificationFactor"
                :readOnly="readOnly"
                :canDelete="!!deleteAction"
                :factor="currentClassificationFactor"
                :levels="classificationLevelsForCurrentFactor"
                :all-levels="allClassificationLevelsForCurrentFactor"
                :ceiling-note="ceilingNote"
                :current-index="currentClassificationIndex"
                :total-factors="6"
                :previous-answer="previousClassificationAnswer"
                :editing="editingFromSummary"
                :rationale-draft="rationaleDraftContext"
                @answer="handleClassificationAnswer"
                @back="handleClassificationBack"
                @cancel="cancelClassificationEdit"
                @restart="promptRestart"
                @delete="promptDelete"
              />

              <!-- Guided Flow: Question -->
              <GuidedQuestion
                v-else-if="viewState === 'guided-question' && currentGuidedQuestion"
                :readOnly="readOnly"
                :canDelete="!!deleteAction"
                :question="currentGuidedQuestion"
                :current-index="currentGuidedIndex"
                :total-questions="visibleQuestionIds.length"
                :previous-answer="previousGuidedAnswer"
                @answer="handleGuidedAnswer"
                @back="handleGuidedBack"
                @restart="promptRestart"
                @delete="promptDelete"
              />

              <!-- Summary -->
              <EvaluationSummary
                v-else-if="viewState === 'summary'"
                :evaluation="evaluation"
                :readOnly="readOnly"
                :canDelete="!!deleteAction"
                :levels="levels"
                :factors="factors"
                :functionTrackLevels="functionTrackLevels"
                :tracks="tracks"
                :functions="functions"
                :selectedFunction="selectedFunction"
                :selectedTrack="selectedTrack"
                :can-edit-track="askableQuestions.some(q => q.no === 2)"
                :answers="answers"
                :classification-answers="classificationAnswers"
                :can-anchor="canGovernAnchors"
                :ceiling="ceiling"
                :anchors="anchorsRaw"
                :doc-name="doc ? doc.doc_name : null"
                :doc-id="doc ? doc.doc_id : null"
                @restart="promptRestart"
                @evaluateToAnchor="restartAsAnchor"
                @delete="promptDelete"
                @save="summaryUpdated"
                @complete="complete"
                @compare="openComparison"
                @editSubFactor="editSubFactor"
                @editClassificationFactor="editClassificationFactor"
                @editTrack="editTrack"
                @changeTrack="changeTrack"
                @changeFunction="changeFunction"
                @reviewAnswers="reviewAnswers"
                @openActivity="openActivity"
              />

              <EvaluationCompare
                v-else-if="viewState === 'comparison'"
                :initialTab="comparisonInitialTab"
                :evaluation="evaluation"
                :doc="doc"
                :functions="functions"
                :tracks="tracks"
                :factors="classificationFactors"
                :levels="levels"
                :selected-level-abbreviation="selectedLevelAbbreviation"
                @back="viewState = 'summary'"
                @openDocument="$emit('openDocument', $event)"
                @copyDocument="$emit('copyDocument', $event)"
              />

              <div v-else-if="viewState === 'thankyou'" class="lvl-qcard">
                <div class="lvl-qcard-in lvl-welcome">
                  <div class="lvl-welcome-head">
                    <span class="lvl-welcome-ic lvl-welcome-ic--done"><span class="ms fill" style="font-size:24px">check_circle</span></span>
                    <h2 class="lvl-q-title">{{ thankYouTitle }}</h2>
                  </div>
                  <p class="lvl-q-desc">{{ thankYouMessage }}</p>
                </div>
                <div class="lvl-cardfoot">
                  <span class="restart"></span>
                  <span class="prog"></span>
                  <span class="nav"><button type="button" class="lvl-btn" @click="finish">Finish</button></span>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <template v-if="showDocDetails">
          <v-col :cols="docDetailsExpanded ? 7 : 5" v-if="doc">
            <v-card class="ai-assist-card">
              <v-card-text class="pa-3">
                <div class="ai-assist-header">
                  <!-- AI Assist toggle: top-left of the panel -->
                  <button
                    v-if="aiAssist.showAiAssistToggle.value"
                    type="button"
                    class="ai-assist-header-toggle"
                    :class="{ 'ai-assist-header-toggle--active': aiAssist.showAiAssist.value }"
                    @click="aiAssist.toggleAiAssist(!aiAssist.showAiAssist.value)"
                  >
                    <span class="header-toggle-track">
                      <span class="header-toggle-knob"></span>
                    </span>
                    <span class="ms" style="font-size:15px">explore</span>
                    AI Assist
                  </button>
                  <span v-if="aiAssist.showAiAssistPanel.value" class="ai-assist-status">
                    <template v-if="aiAssist.currentFactorAiState.value && aiAssist.currentFactorAiState.value.state === 'executing'">
                      <v-progress-circular indeterminate size="14" width="2" color="#4da6ff" class="mr-2" />
                      Analysing...
                    </template>
                    <template v-else-if="aiAssist.currentFactorAiState.value && aiAssist.currentFactorAiState.value.state === 'completed' && aiAssist.highlightedSentences.value.length">
                      {{ aiAssist.highlightedSentences.value.length }} match{{ aiAssist.highlightedSentences.value.length === 1 ? '' : 'es' }} found
                    </template>
                    <template v-else-if="aiAssist.currentFactorAiState.value && aiAssist.currentFactorAiState.value.state === 'completed'">
                      No matches found
                    </template>
                    <template v-else-if="aiAssist.currentFactorAiState.value && aiAssist.currentFactorAiState.value.state === 'failed'">
                      Analysis unavailable
                    </template>
                  </span>
                  <div class="ai-assist-header-spacer"></div>
                  <!-- Full / Sentence view controls: top-right -->
                  <div v-if="aiAssist.showAiAssistPanel.value" class="ai-assist-controls">
                    <div class="ai-assist-view-toggle">
                      <button
                        class="ai-assist-view-btn"
                        :class="{ 'ai-assist-view-btn--active': aiAssist.aiAssistDisplayMode.value === 'full' }"
                        @click="aiAssist.aiAssistDisplayMode.value = 'full'"
                      >
                        <span class="ms">description</span>
                        In Document
                      </button>
                      <button
                        class="ai-assist-view-btn"
                        :class="{ 'ai-assist-view-btn--active': aiAssist.aiAssistDisplayMode.value === 'sentences' }"
                        :disabled="!aiAssist.highlightedSentences.value.length"
                        @click="aiAssist.aiAssistDisplayMode.value = 'sentences'"
                      >
                        <span class="ms">format_list_bulleted</span>
                        Evidence list
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Panel header: source name + expand/collapse toggle -->
                <div class="doc-detail-header">
                  <div class="doc-detail-header-info">
                    <div class="doc-detail-title" :title="docHeaderTitle">{{ docHeaderTitle }}</div>
                    <div class="doc-detail-sub">
                      <span class="ms">{{ viewState === 'welcome' && welcomePreview.contentHtml || contentSource.type === 'file' ? 'description' : 'article' }}</span>
                      <span>{{ docHeaderSubtitle }}</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    class="lvl-jp-widen"
                    :title="docDetailsExpanded ? 'Narrow the document panel' : 'Widen the document panel'"
                    @click="toggleDocDetailsExpanded"
                  >
                    <span class="ms">{{ docDetailsExpanded ? 'close_fullscreen' : 'open_in_full' }}</span>
                  </button>
                </div>

                <v-row v-if="relatedContentLoading" justify="center" align="center">
                  <v-col cols="auto" class="text-center">
                    <v-progress-circular
                      indeterminate
                      color="primary"
                      size="64"
                    ></v-progress-circular>
                    <p class="mt-4">Loading...</p>
                  </v-col>
                </v-row>
                
                <!-- AI assist on: analysed document + matched sentences -->
                <div v-else-if="aiAssist.showAiAssistPanel.value" class="ai-assist-content-outer">
                  <div class="ai-assist-content-wrapper">
                    <!-- Full document view with inline highlights -->
                    <div v-show="aiAssist.aiAssistDisplayMode.value === 'full'" :ref="el => aiAssist.aiDocSummaryRef.value = el" class="ai-assist-source-text">
                      <template v-if="contentSource.type == 'file'">
                        <div class="file-summary">
                          <div v-html="contentSource.content"></div>
                        </div>
                      </template>
                      <DocSummary v-else :doc_id="doc.doc_id"></DocSummary>
                    </div>
                    <!-- Sentence list view -->
                    <div v-if="aiAssist.aiAssistDisplayMode.value === 'sentences'" class="ai-assist-sentence-list">
                      <div
                        v-for="(item, idx) in aiAssist.highlightedSentences.value"
                        :key="idx"
                        class="ai-assist-sentence-item"
                      >
                        <div class="ai-assist-sentence-header">
                          <span v-if="item.sub_factor" class="ai-assist-sentence-badge">{{ item.sub_factor }}</span>
                          <span v-if="item.confidence != null" class="ai-assist-sentence-conf">{{ Math.round(item.confidence * 100) }}%</span>
                        </div>
                        <div class="ai-assist-sentence-quote">"{{ typeof item === 'string' ? item : item.sentence }}"</div>
                        <div v-if="item.justification" class="ai-assist-sentence-justification">{{ item.justification }}</div>
                      </div>
                    </div>
                  </div>
                </div>
                <!-- AI assist off: plain job document -->
                <div v-else class="ai-assist-content-outer">
                  <div class="ai-assist-content-wrapper ai-assist-source-text">
                    <template v-if="viewState === 'welcome' && welcomePreview.contentHtml">
                      <div class="file-summary">
                        <div v-html="welcomePreview.contentHtml"></div>
                      </div>
                    </template>
                    <template v-else-if="contentSource.type === 'file'">
                      <div class="file-summary">
                        <div v-html="contentSource.content"></div>
                      </div>
                    </template>
                    <DocSummary v-else-if="doc.doc_id" :doc_id="doc.doc_id"></DocSummary>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </template>
      </v-row>
    </div>
    <v-dialog v-model="showRestartConfirm" max-width="460" persistent content-class="lvl-eval">
      <div class="lvl-confirm-card">
        <div class="lvl-confirm-head">
          <span class="lvl-confirm-ic"><span class="ms">restart_alt</span></span>
          <div>
            <h3>Restart the evaluation?</h3>
            <p>This archives the current evaluation &mdash; answers, rationale, and factors &mdash; and takes you back to the first question. The role and its document stay as they are.</p>
          </div>
        </div>
        <div class="lvl-confirm-foot">
          <button type="button" class="lvl-btn lvl-btn--text" @click="showRestartConfirm = false">Cancel</button>
          <button type="button" class="lvl-btn lvl-btn--danger" @click="confirmRestart">
            <span class="ms">restart_alt</span> Restart
          </button>
        </div>
      </div>
    </v-dialog>
    <v-dialog v-model="showDeleteConfirm" max-width="460" persistent content-class="lvl-eval">
      <div class="lvl-confirm-card">
        <div class="lvl-confirm-head">
          <span class="lvl-confirm-ic"><span class="ms">delete</span></span>
          <div>
            <h3>Delete evaluation?</h3>
            <p>This will permanently delete this evaluation. This action cannot be undone.</p>
          </div>
        </div>
        <div class="lvl-confirm-foot">
          <button type="button" class="lvl-btn lvl-btn--text" :disabled="deleting" @click="showDeleteConfirm = false">Cancel</button>
          <button type="button" class="lvl-btn lvl-btn--danger" :class="{ 'is-loading': deleting }" :disabled="deleting" @click="confirmDelete">
            <span class="ms">delete</span> {{ deleting ? 'Deleting…' : 'Delete' }}
          </button>
        </div>
      </div>
    </v-dialog>
    <DocumentActivities
      v-if="doc"
      :open="showActivity"
      :doc_id="doc.doc_id"
      default-tab="levellingEvaluations"
      :expand-evaluation-id="evaluation.id"
      @closed="activityClosed"
      @openDocument="$emit('openDocument', $event)"
      @copyDocument="$emit('copyDocument', $event)"
    ></DocumentActivities>
  </v-container>
</template>

<style lang="scss" src="./evaluationTheme.scss"></style>
<style scoped lang="scss">
/* The holder is a transparent frame; each screen brings its own .lvl-qcard. */
.main-background {
  padding-top: 0;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  min-height: 100%;
}

.main-background.eval-docked {
  background: var(--v3-canvas);
  .evaluation-nav {
    background: var(--v3-canvas);
  }
}

.evaluation-container { width: 100%; }

.evaluation-nav {
  margin-top: 0; /* override Vuetify row's -12px so it pins flush to the top */
  padding-bottom: 4px;
}

.evaluation-card {
  background: transparent !important;
  box-shadow: none !important;
  border-radius: 0 !important;
}
.evaluation-card ::v-deep > .v-card__title { padding: 4px 2px 10px; }
.evaluation-card ::v-deep > .v-card__text { padding: 0; color: var(--v3-text); }

/* let the docked (fixed) action bar reach the viewport bottom without being clipped */
.eval-docked .evaluation-card,
.eval-docked .evaluation-card ::v-deep > .v-card__text { overflow: visible; }

.main-background.eval-docked {
  align-items: stretch;
  padding-bottom: 72px !important;
  &.state-invite,
  &.state-landing,
  &.state-comparison,
  /* The re-evaluate intro keeps its action inside the card (see evaluationTheme's
     un-dock rule), so there is no fixed bar to reserve room for — the 72px would
     just be dead space under a short card. */
  &.state-welcome.sub-reIntro {
    padding-bottom: 0 !important;
  }
}

.evaluation-holder {
  box-shadow: none !important;
  ::v-deep .v-card__title { padding: 6px 2px 10px !important; }
  ::v-deep .v-card__text { padding: 0 !important; }
}

.links-col {
  margin-top: 8px;
  align-self: center;
  flex-grow: 0;
}
.lvl-backchip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px 6px 10px;
  background: var(--v3-surface);
  border: 1px solid var(--v3-border);
  border-radius: 8px;
  box-shadow: var(--v3-shadow);
  font: inherit;
  font-size: 13.5px;
  font-weight: 600;
  color: var(--v3-text);
  cursor: pointer;
  white-space: nowrap;
  transition: border-color 0.12s, box-shadow 0.12s;
}
.lvl-backchip:hover { border-color: var(--v3-border-strong); }
.lvl-backchip:focus-visible { outline: 2px solid var(--v3-accent); outline-offset: 2px; }
.lvl-backchip .ms { font-size: 17px; color: var(--v3-text-3); flex: 0 0 auto; }

/* Top bar: back (left) · overall stepper (centre) · document link (right) */
.eval-topbar { display: flex; align-items: center; gap: 16px; width: 100%; }
.eval-topbar-left { flex: 1 1 0; min-width: 0; display: flex; align-items: center; }
.eval-topbar-center { flex: 0 1 auto; min-width: 0; display: flex; justify-content: center; }
.eval-topbar-right { flex: 1 1 0; min-width: 0; display: flex; justify-content: flex-end; }
.eval-topbar-center .lvl-flowsteps { margin: 0; max-width: none; padding: 0; }
.eval-topbar-step { font-size: 14px; font-weight: 600; color: var(--v3-text); }

/* Guidance drawer → info note */
.guidance-panel {
  margin: 0 0 16px;
  padding: 13px 16px;
  border-radius: var(--v3-r-md);
  background: color-mix(in srgb, var(--v3-accent) 7%, var(--v3-tint));
  color: var(--v3-text-2);
  font-size: 13.5px;
  line-height: 1.55;
}

.evaluation-row {
  max-width: 1600px;
  margin-left: auto;
  margin-right: auto;
}

.state-invite {
  .evaluation-row {
    max-width: 650px;
  }
}
.state-loading,
.state-thankyou,
.state-error,
.state-welcome.hide-doc-details {
  .evaluation-row {
    max-width: 880px;
  }
}
.state-landing,
.state-summary,
.state-comparison {
  .evaluation-row {
    max-width: 1400px;
  }
}

/* These are the single-column states (no doc panel), where the content is
   often shorter than the viewport. Vertically centre the card in the space
   below the sticky top bar. Auto margins (rather than align-items:center) so
   that when the content is taller than the viewport — e.g. a long summary or
   comparison — it falls back to top-aligned and stays fully scrollable. */
.state-invite,
.state-loading,
.state-thankyou,
.state-error,
.state-landing,
.state-welcome.hide-doc-details {
  .evaluation-container {
    display: flex;
    flex-direction: column;
    min-height: 100%;
  }
  .evaluation-nav {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
  }
  .evaluation-row {
    margin-top: auto;
    margin-bottom: auto;
  }
}

/* ---- side panels (JD / AI assist) ---- */
.document-summary-card {
  border: 1px solid var(--v3-border);
  border-radius: var(--v3-r-lg) !important;
  box-shadow: var(--v3-shadow) !important;
  background: var(--v3-surface);
  padding: 22px 24px;
  max-height: calc(100vh - 160px);
  overflow-y: auto;
  color: var(--v3-text-2) !important;
}

.ai-assist-card {
  border: 1px solid var(--v3-border);
  border-radius: var(--v3-r-lg) !important;
  box-shadow: var(--v3-shadow) !important;
  background: var(--v3-surface);
  height: calc(100vh - 160px);
  display: flex !important;
  flex-direction: column !important;
  overflow: hidden;
  padding: 14px 16px;

  ::v-deep > .v-card__text {
    display: flex !important;
    flex-direction: column !important;
    flex: 1 1 0% !important;
    min-height: 0 !important;
    overflow: hidden !important;
    padding: 4px !important;
  }
}

.doc-detail-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--v3-border);
}
.doc-detail-header-info { flex: 1 1 auto; min-width: 0; }
.doc-detail-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--v3-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.doc-detail-sub {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
  font-size: 12px;
  color: var(--v3-text-3);
}
.doc-detail-sub .ms { font-size: 15px; }

.ai-assist-header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}
.ai-assist-status {
  flex: 0 1 auto;
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  color: var(--v3-accent);
  font-weight: 500;
  min-width: 0;
  margin-left: 6px;
}
.ai-assist-controls { display: flex; align-items: center; gap: 6px; }
.ai-assist-header-spacer { flex: 1; }

.ai-assist-view-toggle {
  display: inline-flex;
  align-items: center;
  background: var(--v3-surface-2);
  border: 1px solid var(--v3-border);
  border-radius: 999px;
  padding: 3px;
}
.ai-assist-view-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 2px 12px;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: var(--v3-text-2);
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;

  .v-icon { color: inherit !important; }
  .ms { font-size: 15px; }
  &:hover:not(:disabled) { color: var(--v3-text); }
  &--active {
    color: var(--v3-accent);
    background: var(--v3-surface);
    box-shadow: var(--v3-shadow);
    &:hover:not(:disabled) { color: var(--v3-accent); background: var(--v3-surface); }
  }
  &:disabled { opacity: 0.4; cursor: default; }
}

.ai-assist-highlight-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--v3-text-3);
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) { background: var(--v3-surface-2); color: var(--v3-text-2); }
  &--active { color: var(--v3-accent); background: color-mix(in srgb, var(--v3-accent) 10%, var(--v3-tint)); }
  &:disabled { opacity: 0.35; cursor: default; }
}

.ai-assist-content-outer { position: relative; flex: 1; min-height: 0; }
.ai-assist-content-wrapper { height: 100%; overflow-y: auto; overflow-x: hidden; }
.ai-assist-source-text { font-size: 13px; color: var(--v3-text-2); line-height: 1.7; }

.ai-assist-sentence-list { padding: 0; }
.ai-assist-sentence-item {
  padding: 12px 14px;
  border-left: 3px solid var(--v3-border);
  margin: 0 4px 12px;
  background: var(--v3-surface-2);
  border-radius: 0 var(--v3-r-sm) var(--v3-r-sm) 0;
  transition: border-color 0.15s ease;
  &:hover { border-left-color: var(--v3-accent); }
}
.ai-assist-sentence-header { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
.ai-assist-sentence-badge {
  display: inline-block;
  font-size: 11px;
  font-weight: 650;
  color: var(--v3-accent);
  background: color-mix(in srgb, var(--v3-accent) 10%, var(--v3-tint));
  padding: 2px 8px;
  border-radius: 999px;
  line-height: 1.4;
}
.ai-assist-sentence-conf { font-size: 11px; font-weight: 500; color: var(--v3-text-3); }
.ai-assist-sentence-quote { font-size: 13px; color: var(--v3-text); line-height: 1.6; font-style: italic; }
.ai-assist-sentence-justification {
  font-size: 12px;
  color: var(--v3-text-2);
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid var(--v3-border-soft);
  line-height: 1.5;
}

/* Header AI-assist toggle (Atlas indigo) */
.ai-assist-header-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 2px 12px 2px 5px;
  font-size: 12.5px;
  font-weight: 650;
  color: var(--v3-text-2);
  background: transparent;
  border: 1px solid var(--v3-border);
  border-radius: 999px;
  cursor: pointer;
  user-select: none;
  transition: all 0.15s ease;
  margin-right: 6px;

  &:hover { border-color: var(--v3-border-strong); }
  &--active {
    color: var(--v3-accent);
    background: color-mix(in srgb, var(--v3-accent) 8%, var(--v3-tint));
    border-color: color-mix(in srgb, var(--v3-accent) 35%, var(--v3-tint));
  }
}
.header-toggle-track {
  position: relative;
  width: 36px;
  height: 20px;
  background: var(--v3-border-strong);
  border-radius: 999px;
  transition: background 0.15s ease;
  flex-shrink: 0;
}
.ai-assist-header-toggle--active .header-toggle-track { background: var(--v3-accent); }
.header-toggle-knob {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  background: #fff;
  border-radius: 50%;
  transition: transform 0.15s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}
.ai-assist-header-toggle--active .header-toggle-knob { transform: translateX(16px); }

/* Delete confirmation (teleported dialog content is wrapped in .lvl-eval) */
.lvl-confirm-card {
  background: var(--v3-surface);
  border-radius: var(--v3-r-xl);
  box-shadow: var(--v3-shadow-lg);
  overflow: hidden;
}
.lvl-confirm-head { display: flex; align-items: flex-start; gap: 14px; padding: 22px 24px 6px; }
.lvl-confirm-ic {
  flex: 0 0 auto;
  width: 40px; height: 40px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  background: var(--v3-attn-bg);
  color: var(--v3-attn);
}
.lvl-confirm-head h3 { margin: 0; font-size: 17px; font-weight: 700; color: var(--v3-text); }
.lvl-confirm-head p { margin: 5px 0 0; font-size: 13.5px; line-height: 1.5; color: var(--v3-text-2); }
.lvl-confirm-foot { display: flex; justify-content: flex-end; gap: 10px; padding: 16px 24px 20px; }
</style>
<style lang="scss">
.lvl-eval .ai-highlight,
.ai-highlight {
  background-color: color-mix(in srgb, var(--v3-accent, #4f46e5) 22%, transparent);
  border-bottom: 2px solid var(--v3-accent, #4f46e5);
  padding: 1px 2px;
  border-radius: 2px;
  position: relative;
}
.ai-highlight-tooltip {
  position: fixed;
  font-family: 'Inter', sans-serif;
  background: #1f242d;
  color: #fff;
  font-size: 12px;
  line-height: 1.4;
  padding: 8px 12px;
  border-radius: 6px;
  white-space: pre-wrap;
  max-width: 350px;
  min-width: 200px;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
  pointer-events: none;
}
</style>